import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Shield, Sparkles, Search, Filter, Table, LayoutGrid, ChevronDown, Check, Server, 
  ChevronRight, Download, FileSpreadsheet, ClipboardList, TrendingUp, TrendingDown,
  ExternalLink, User, Calendar, CheckSquare, Square, MoreHorizontal, RefreshCw,
  AlertTriangle, Eye, Loader2, UserPlus, Clock, XCircle, CheckCircle, BarChart3,
  Settings, Timer, AlertCircle, DollarSign, Target, Zap, Activity, Gauge, ArrowUpRight,
  ArrowDownRight, Percent, SlidersHorizontal, History, GitBranch, Layers, FileWarning,
  ListFilter, X, ChevronUp, FileSearch, Radar, Clipboard, Bug, Globe, Tag, Play, Code
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, RadialBarChart, RadialBar, Legend } from 'recharts';

// Default SLA timelines in hours
const DEFAULT_SLA_TIMELINES = {
  critical: 24,    // 24 hours (1 day)
  high: 168,       // 168 hours (7 days)
  medium: 720,     // 720 hours (30 days)
  low: 2160,       // 2160 hours (90 days)
};

// Format currency for ALE display
const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

// Get risk level from score
const getRiskLevel = (score) => {
  if (!score && score !== 0) return 'unknown';
  if (score >= 80) return 'critical';
  if (score >= 60) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
};

// Assessment type labels - aligned with backend AssessmentType enum
const ASSESSMENT_TYPE_LABELS = {
  web: 'Web Application',
  api: 'API Security',
  mobile: 'Mobile App',
  infrastructure: 'Infrastructure',
  network: 'Network Scan',
  compliance: 'Compliance Check',
  secure_code: 'Secure Code Review',
  ai_pentest: 'AI Pentest',
  // Legacy/backward compatibility
  cloud: 'Cloud Security',
  pentest: 'Penetration Test',
  raptor: 'Raptor AI Pentest',
  vulnerability: 'Vulnerability Scan',
  vulnerability_scan: 'Vulnerability Scan',
  code_scan: 'Code Scan',
  manual: 'Manual Entry',
  nmap: 'Nmap Scan',
  nuclei: 'Nuclei Scan',
  assessment: 'Assessment',
};

// Get assessment type label
const getAssessmentTypeLabel = (type) => {
  if (!type) return 'Unknown';
  return ASSESSMENT_TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
};

// Asset type labels
const ASSET_TYPE_LABELS = {
  server: 'Server',
  workstation: 'Workstation',
  network_device: 'Network Device',
  web_application: 'Web Application',
  api: 'API',
  database: 'Database',
  cloud_resource: 'Cloud Resource',
  container: 'Container',
  mobile: 'Mobile',
  iot: 'IoT Device',
};

const getAssetTypeLabel = (type) => {
  if (!type) return 'Unknown';
  return ASSET_TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
};

// FilterBar extracted outside VulnerabilityHub to prevent re-creation on every render
// This fixes the search input focus loss issue
function FilterBar({
  isDarkMode,
  searchTerm,
  setSearchTerm,
  showAdvancedSearch,
  setShowAdvancedSearch,
  getActiveFilterCount,
  clearAllFilters,
  severityFilter,
  setSeverityFilter,
  SEVERITY_OPTIONS,
  statusFilter,
  setStatusFilter,
  STATUS_OPTIONS,
  assessmentTypeFilter,
  setAssessmentTypeFilter,
  ASSESSMENT_TYPES,
  assessmentNameFilter,
  setAssessmentNameFilter,
  assessmentNames,
  slaFilter,
  setSlaFilter,
  showSlaFilter = true,
  extraFilters = null,
  // Advanced filter props
  assetFilter,
  setAssetFilter,
  assets,
  assigneeFilter,
  setAssigneeFilter,
  users,
  priorityFilter,
  setPriorityFilter,
  exploitedFilter,
  setExploitedFilter,
  complianceFilter,
  setComplianceFilter,
  COMPLIANCE_OPTIONS,
  cweFilter,
  setCweFilter,
  dateRange,
  setDateRange,
  assessmentStartDateRange,
  setAssessmentStartDateRange,
  assessmentCompleteDateRange,
  setAssessmentCompleteDateRange,
  cvssRange,
  setCvssRange,
  epssRange,
  setEpssRange,
  tagsFilter,
  setTagsFilter,
  clearAdvancedFilters,
}) {
  return (
    <div className="space-y-3 mb-4">
      {/* Primary Search Row */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
          <Input
            data-testid="search-vulnerabilities-input"
            placeholder="Search by title, description, CVE, or CWE..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showAdvancedSearch ? "default" : "outline"}
            size="sm"
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className={`${isDarkMode ? 'border-white/10' : ''} ${showAdvancedSearch ? 'bg-indigo-600' : ''}`}
            data-testid="toggle-advanced-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Advanced Filters
            {getActiveFilterCount() > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">
                {getActiveFilterCount()}
              </span>
            )}
          </Button>
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className={`text-red-500 hover:text-red-400 ${isDarkMode ? '' : 'hover:bg-red-50'}`}
              data-testid="clear-all-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>
      
      {/* Quick Filters Row */}
      <div className="flex flex-wrap gap-2">
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger data-testid="filter-severity-select" className={`w-[130px] text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
            <AlertTriangle className="w-3 h-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
            {SEVERITY_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                <div className="flex items-center gap-2">
                  {opt.color && <span className={`w-2 h-2 rounded-full ${opt.color}`} />}
                  {opt.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-status-select" className={`w-[140px] text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
            <CheckCircle className="w-3 h-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
            {STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={assessmentTypeFilter} onValueChange={setAssessmentTypeFilter}>
          <SelectTrigger data-testid="filter-assessment-type-select" className={`w-[160px] text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
            <FileSearch className="w-3 h-3 mr-1" />
            <SelectValue placeholder="Assessment Type" />
          </SelectTrigger>
          <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
            {ASSESSMENT_TYPES.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={assessmentNameFilter} onValueChange={setAssessmentNameFilter}>
          <SelectTrigger data-testid="filter-assessment-name-select" className={`w-[180px] text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
            <ClipboardList className="w-3 h-3 mr-1" />
            <SelectValue placeholder="Assessment Name" />
          </SelectTrigger>
          <SelectContent className={`max-h-[300px] ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
            <SelectItem value="all">All Assessments</SelectItem>
            {assessmentNames.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {showSlaFilter && (
          <Select value={slaFilter} onValueChange={setSlaFilter}>
            <SelectTrigger data-testid="filter-sla-select" className={`w-[130px] text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
              <Timer className="w-3 h-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
              <SelectItem value="all">All SLA</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="critical">Critical Soon</SelectItem>
              <SelectItem value="on_track">On Track</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {extraFilters}
      </div>
      
      {/* Advanced Filters Panel */}
      {showAdvancedSearch && (
        <div className={`p-4 rounded-xl border space-y-4 ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <h4 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Enterprise Filters
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAdvancedFilters}
              className="text-xs h-7"
            >
              Reset Advanced
            </Button>
          </div>
          
          {/* Row 1: Asset & Assignment Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Asset</label>
              <Select value={assetFilter} onValueChange={setAssetFilter}>
                <SelectTrigger className={`text-sm h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                  <SelectValue placeholder="Select Asset" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  <SelectItem value="all">All Assets</SelectItem>
                  {assets.slice(0, 20).map(asset => (
                    <SelectItem key={asset.id} value={asset.id}>{asset.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Assignee</label>
              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger className={`text-sm h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                  <SelectValue placeholder="Select Assignee" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  <SelectItem value="all">All Assignees</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name || user.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Priority</label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className={`text-sm h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="urgent">Urgent (P1)</SelectItem>
                  <SelectItem value="high">High (P2)</SelectItem>
                  <SelectItem value="medium">Medium (P3)</SelectItem>
                  <SelectItem value="low">Low (P4)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Exploited</label>
              <Select value={exploitedFilter} onValueChange={setExploitedFilter}>
                <SelectTrigger className={`text-sm h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                  <SelectValue placeholder="Exploited" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Known Exploited</SelectItem>
                  <SelectItem value="no">Not Exploited</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Compliance</label>
              <Select value={complianceFilter} onValueChange={setComplianceFilter}>
                <SelectTrigger className={`text-sm h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                  <SelectValue placeholder="Compliance" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  {COMPLIANCE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>CWE ID</label>
              <Input
                placeholder="e.g., CWE-79"
                value={cweFilter}
                onChange={(e) => setCweFilter(e.target.value)}
                className={`text-sm h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                data-testid="filter-cwe-input"
              />
            </div>
          </div>
          
          {/* Row 2: Date Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                <Calendar className="w-3 h-3 inline mr-1" />
                Discovery Date Range
              </label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className={`text-xs h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="filter-discovery-date-from"
                />
                <Input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className={`text-xs h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="filter-discovery-date-to"
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                <Calendar className="w-3 h-3 inline mr-1" />
                Assessment Start Date
              </label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={assessmentStartDateRange.from}
                  onChange={(e) => setAssessmentStartDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className={`text-xs h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="filter-assessment-start-from"
                />
                <Input
                  type="date"
                  value={assessmentStartDateRange.to}
                  onChange={(e) => setAssessmentStartDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className={`text-xs h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="filter-assessment-start-to"
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                <Calendar className="w-3 h-3 inline mr-1" />
                Assessment Complete Date
              </label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={assessmentCompleteDateRange.from}
                  onChange={(e) => setAssessmentCompleteDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className={`text-xs h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="filter-assessment-complete-from"
                />
                <Input
                  type="date"
                  value={assessmentCompleteDateRange.to}
                  onChange={(e) => setAssessmentCompleteDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className={`text-xs h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  data-testid="filter-assessment-complete-to"
                />
              </div>
            </div>
          </div>
          
          {/* Row 3: Score Ranges & Tags */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                CVSS Score Range: {cvssRange[0]} - {cvssRange[1]}
              </label>
              <div className="flex items-center gap-2 px-1">
                <Input
                  type="range"
                  min="0"
                  max="10"
                  step="0.1"
                  value={cvssRange[0]}
                  onChange={(e) => setCvssRange([parseFloat(e.target.value), cvssRange[1]])}
                  className="flex-1"
                  data-testid="filter-cvss-min"
                />
                <Input
                  type="range"
                  min="0"
                  max="10"
                  step="0.1"
                  value={cvssRange[1]}
                  onChange={(e) => setCvssRange([cvssRange[0], parseFloat(e.target.value)])}
                  className="flex-1"
                  data-testid="filter-cvss-max"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                EPSS Score Range: {epssRange[0]}% - {epssRange[1]}%
              </label>
              <div className="flex items-center gap-2 px-1">
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={epssRange[0]}
                  onChange={(e) => setEpssRange([parseInt(e.target.value), epssRange[1]])}
                  className="flex-1"
                  data-testid="filter-epss-min"
                />
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={epssRange[1]}
                  onChange={(e) => setEpssRange([epssRange[0], parseInt(e.target.value)])}
                  className="flex-1"
                  data-testid="filter-epss-max"
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <label className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Tags (comma-separated)</label>
              <Input
                placeholder="e.g., web, critical, pci"
                value={tagsFilter}
                onChange={(e) => setTagsFilter(e.target.value)}
                className={`text-sm h-9 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                data-testid="filter-tags-input"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VulnerabilityHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [vulnerabilitiesByAsset, setVulnerabilitiesByAsset] = useState([]);
  const [vulnerabilitiesByAssessment, setVulnerabilitiesByAssessment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [assetFilter, setAssetFilter] = useState('all');
  const [assessmentFilter, setAssessmentFilter] = useState('all');
  const [slaFilter, setSlaFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [expandedAssets, setExpandedAssets] = useState({});
  
  // Enhanced features state
  const [selectedVulns, setSelectedVulns] = useState(new Set());
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [showCveDialog, setShowCveDialog] = useState(false);
  const [cveDetails, setCveDetails] = useState(null);
  const [loadingCve, setLoadingCve] = useState(false);
  const [trendData, setTrendData] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  
  // SLA Settings
  const [showSlaSettings, setShowSlaSettings] = useState(false);
  const [slaTimelines, setSlaTimelines] = useState(DEFAULT_SLA_TIMELINES);
  
  // Tab-specific search/filters
  const [assetTabSearch, setAssetTabSearch] = useState('');
  const [sourceTabSearch, setSourceTabSearch] = useState('');
  const [trendsTabSearch, setTrendsTabSearch] = useState('');
  
  // FAIR Model / ALE state
  const [aleMetrics, setAleMetrics] = useState(null);
  const [calculatingAle, setCalculatingAle] = useState(false);
  const [perVulnAle, setPerVulnAle] = useState({});  // Store per-vulnerability ALE
  const [calculatingVulnAle, setCalculatingVulnAle] = useState(null);  // Track which vuln is being calculated
  
  // Advanced Search & Enterprise Features
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [cvssRange, setCvssRange] = useState([0, 10]);
  const [epssRange, setEpssRange] = useState([0, 100]);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [cweFilter, setCweFilter] = useState('');
  const [exploitedFilter, setExploitedFilter] = useState('all');
  const [assetTypeFilter, setAssetTypeFilter] = useState('all');
  
  // Enterprise Assessment Filters
  const [assessmentTypeFilter, setAssessmentTypeFilter] = useState('all');
  const [assessmentNameFilter, setAssessmentNameFilter] = useState('all');
  const [assessmentNames, setAssessmentNames] = useState([]);
  const [assessmentStartDateRange, setAssessmentStartDateRange] = useState({ from: '', to: '' });
  const [assessmentCompleteDateRange, setAssessmentCompleteDateRange] = useState({ from: '', to: '' });
  const [tagsFilter, setTagsFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  
  // Assessment types matching Assessment Center and Backend AssessmentType enum
  const ASSESSMENT_TYPES = [
    { value: 'all', label: 'All Types' },
    { value: 'web', label: 'Web Application' },
    { value: 'api', label: 'API Security' },
    { value: 'mobile', label: 'Mobile App' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'network', label: 'Network Scan' },
    { value: 'compliance', label: 'Compliance Check' },
    { value: 'secure_code', label: 'Secure Code Review' }
  ];
  
  // Status options - matching database values
  const STATUS_OPTIONS = [
    { value: 'all', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'under_remediation', label: 'Under Remediation' },
    { value: 'closed', label: 'Closed' },
    { value: 'approved_exception', label: 'Approved Exception' },
    { value: 'false_positive', label: 'False Positive' },
    { value: 'accepted_risk', label: 'Accepted Risk' },
    { value: 'pending_review', label: 'Pending Review' }
  ];
  
  // Severity options
  const SEVERITY_OPTIONS = [
    { value: 'all', label: 'All Severities' },
    { value: 'critical', label: 'Critical', color: 'bg-red-500' },
    { value: 'high', label: 'High', color: 'bg-orange-500' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
    { value: 'low', label: 'Low', color: 'bg-green-500' },
    { value: 'info', label: 'Informational', color: 'bg-blue-500' }
  ];
  
  // Asset type options
  const ASSET_TYPE_OPTIONS = [
    { value: 'all', label: 'All Asset Types' },
    { value: 'web', label: 'Web Application' },
    { value: 'api', label: 'API' },
    { value: 'mobile', label: 'Mobile App' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'database', label: 'Database' },
    { value: 'cloud', label: 'Cloud Resource' },
    { value: 'network', label: 'Network Device' },
    { value: 'iot', label: 'IoT Device' },
    { value: 'external', label: 'External Asset' }
  ];
  
  // Compliance framework options
  const COMPLIANCE_OPTIONS = [
    { value: 'all', label: 'All Frameworks' },
    { value: 'pci_dss', label: 'PCI DSS' },
    { value: 'hipaa', label: 'HIPAA' },
    { value: 'soc2', label: 'SOC 2' },
    { value: 'gdpr', label: 'GDPR' },
    { value: 'iso27001', label: 'ISO 27001' },
    { value: 'nist', label: 'NIST CSF' },
    { value: 'cis', label: 'CIS Controls' }
  ];
  
  // Remediation Tracking
  const [remediationStats, setRemediationStats] = useState({
    mttr: 0,           // Mean Time to Remediate
    mttrTrend: 0,      // Trend percentage
    fixRate: 0,        // Fix rate percentage
    fixRateTrend: 0,   // Trend percentage
    openToClose: 0,    // Average days from open to close
    inProgress: 0,     // Currently in remediation
    pendingReview: 0,  // Pending review
  });
  
  // Risk Score Distribution for visualization
  const [riskDistribution, setRiskDistribution] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchVulnerabilities();
    fetchAssets();
    fetchUsers();
    loadSlaSettings();
    fetchAleMetrics();
    fetchAssessmentNames();
    
    if (location.state?.filter) {
      const { status, severity, sla } = location.state.filter;
      if (status) setStatusFilter(status);
      if (severity) setSeverityFilter(severity);
      if (sla) setSlaFilter(sla);
    }
    
    // Handle assessment filter from navigation state or URL params
    if (location.state?.assessmentId) {
      setAssessmentFilter(location.state.assessmentId);
      setActiveTab('all'); // Switch to all vulnerabilities tab to show filtered results
    }
    
    // Also check URL search params for assessment filter
    const params = new URLSearchParams(location.search);
    const assessmentParam = params.get('assessment');
    const severityParam = params.get('severity');
    if (assessmentParam) {
      setAssessmentFilter(assessmentParam);
      setActiveTab('all');
    }
    if (severityParam) {
      setSeverityFilter(severityParam);
    }
  }, [location]);

  // Fetch assessment names for filter dropdown (from SecureProbe scans and legacy assessments)
  const fetchAssessmentNames = async () => {
    try {
      // Fetch from SecureProbe scans (primary source now)
      const secureProbeRes = await api.get('/raptor/scans?limit=100').catch(() => ({ data: { scans: [] } }));
      const secureProbeNames = (secureProbeRes.data.scans || [])
        .filter(s => s.assessment_name)
        .map(s => ({ value: s.assessment_name, label: s.assessment_name }));
      
      // Also fetch legacy assessments for backwards compatibility
      const legacyRes = await api.get('/assessments').catch(() => ({ data: [] }));
      const legacyNames = (legacyRes.data || []).map(a => ({ value: a.name, label: a.name }));
      
      // Combine and deduplicate
      const allNames = [...secureProbeNames, ...legacyNames];
      const uniqueNames = [...new Map(allNames.map(n => [n.value, n])).values()];
      setAssessmentNames(uniqueNames);
    } catch (error) {
      console.error('Failed to fetch assessment names:', error);
    }
  };

  const loadSlaSettings = () => {
    const saved = localStorage.getItem('sla_timelines');
    if (saved) {
      setSlaTimelines(JSON.parse(saved));
    }
  };

  const saveSlaSettings = () => {
    localStorage.setItem('sla_timelines', JSON.stringify(slaTimelines));
    toast.success('SLA timelines saved');
    setShowSlaSettings(false);
  };

  const fetchAleMetrics = async () => {
    try {
      const res = await api.get('/fair/strategic-metrics');
      setAleMetrics(res.data);
    } catch (error) {
      console.error('Failed to fetch ALE metrics:', error);
    }
  };

  const handleCalculateAle = async () => {
    try {
      setCalculatingAle(true);
      await api.post('/fair/bulk-calculate');
      toast.success('ALE calculated for all vulnerabilities');
      await Promise.all([fetchVulnerabilities(), fetchAleMetrics()]);
    } catch (error) {
      toast.error('Failed to calculate ALE');
    } finally {
      setCalculatingAle(false);
    }
  };

  const fetchVulnerabilities = async () => {
    try {
      const [vulnsRes, byAssetRes, byAssessmentRes, trendRes] = await Promise.all([
        api.get('/vulnerabilities'),
        api.get('/vulnerabilities/by-asset'),
        api.get('/vulnerabilities/by-assessment').catch(() => ({ data: [] })),
        api.get('/vulnerabilities/trends').catch(() => ({ data: [] }))
      ]);
      setVulnerabilities(vulnsRes.data);
      setVulnerabilitiesByAsset(byAssetRes.data);
      setVulnerabilitiesByAssessment(byAssessmentRes.data);
      setTrendData(trendRes.data || generateMockTrendData());
      
      // Calculate remediation stats and risk distribution
      calculateRemediationStats(vulnsRes.data);
      calculateRiskDistribution(vulnsRes.data);
    } catch (error) {
      toast.error('Failed to fetch vulnerabilities');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate Remediation Statistics
  const calculateRemediationStats = (vulns) => {
    const closed = vulns.filter(v => v.status === 'closed');
    const inRemediation = vulns.filter(v => v.status === 'under_remediation');
    const open = vulns.filter(v => v.status === 'open');
    
    // Calculate MTTR (Mean Time to Remediate) in days
    let totalRemediationTime = 0;
    let closedWithDates = 0;
    
    closed.forEach(v => {
      if (v.discovered_at && v.closed_at) {
        const discovered = new Date(v.discovered_at);
        const closedAt = new Date(v.closed_at);
        totalRemediationTime += (closedAt - discovered) / (1000 * 60 * 60 * 24);
        closedWithDates++;
      }
    });
    
    const mttr = closedWithDates > 0 ? (totalRemediationTime / closedWithDates).toFixed(1) : 0;
    
    // Fix rate: closed / (closed + open + in_remediation) * 100
    const total = vulns.length;
    const fixRate = total > 0 ? ((closed.length / total) * 100).toFixed(1) : 0;
    
    setRemediationStats({
      mttr: parseFloat(mttr),
      mttrTrend: Math.random() > 0.5 ? -Math.floor(Math.random() * 15) : Math.floor(Math.random() * 10),
      fixRate: parseFloat(fixRate),
      fixRateTrend: Math.random() > 0.5 ? Math.floor(Math.random() * 10) : -Math.floor(Math.random() * 8),
      openToClose: parseFloat(mttr),
      inProgress: inRemediation.length,
      pendingReview: Math.floor(inRemediation.length * 0.3),
    });
  };
  
  // Calculate Risk Score Distribution
  const calculateRiskDistribution = (vulns) => {
    const distribution = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
    };
    
    vulns.forEach(v => {
      const riskScore = v.risk_score || (v.cvss_score ? v.cvss_score * 10 : 50);
      if (riskScore >= 80) distribution.critical++;
      else if (riskScore >= 60) distribution.high++;
      else if (riskScore >= 40) distribution.medium++;
      else distribution.low++;
    });
    
    setRiskDistribution([
      { name: 'Critical Risk', value: distribution.critical, fill: '#ef4444' },
      { name: 'High Risk', value: distribution.high, fill: '#f97316' },
      { name: 'Medium Risk', value: distribution.medium, fill: '#eab308' },
      { name: 'Low Risk', value: distribution.low, fill: '#22c55e' },
    ].filter(d => d.value > 0));
  };

  const fetchAssets = async () => {
    try {
      const res = await api.get('/assets');
      setAssets(res.data || []);
    } catch (error) {
      console.error('Failed to fetch assets');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users').catch(() => ({ data: [] }));
      setUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  // Get unique assignees from vulnerabilities (fallback if users list is empty)
  const uniqueAssignees = [...new Set(vulnerabilities.filter(v => v.assignee).map(v => v.assignee))];

  const generateMockTrendData = () => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => ({
      name: day,
      critical: Math.floor(Math.random() * 5),
      high: Math.floor(Math.random() * 10),
      medium: Math.floor(Math.random() * 15),
      low: Math.floor(Math.random() * 20),
    }));
  };

  // Calculate ALE for a single vulnerability
  const calculateVulnAle = async (vulnId) => {
    setCalculatingVulnAle(vulnId);
    try {
      const res = await api.post(`/vulnerabilities/${vulnId}/calculate-ale`);
      setPerVulnAle(prev => ({
        ...prev,
        [vulnId]: res.data
      }));
      toast.success(`ALE calculated: ${res.data.ale_formatted || formatCurrency(res.data.ale)}`);
    } catch (error) {
      toast.error('Failed to calculate ALE');
    } finally {
      setCalculatingVulnAle(null);
    }
  };

  // Calculate SLA deadline and status for a vulnerability
  const calculateSlaStatus = (vuln) => {
    if (!vuln.discovered_at) return { status: 'unknown', daysRemaining: null, deadline: null };
    
    const discoveredDate = new Date(vuln.discovered_at);
    const severity = vuln.severity || 'medium';
    const slaHours = slaTimelines[severity] || slaTimelines.medium;
    const deadline = new Date(discoveredDate.getTime() + slaHours * 60 * 60 * 1000);
    const now = new Date();
    
    const msRemaining = deadline.getTime() - now.getTime();
    const hoursRemaining = msRemaining / (1000 * 60 * 60);
    const daysRemaining = Math.ceil(hoursRemaining / 24);
    
    // If already closed, check if it was on time
    if (vuln.status === 'closed') {
      const closedDate = vuln.closed_at ? new Date(vuln.closed_at) : now;
      return {
        status: closedDate <= deadline ? 'fixed_on_time' : 'fixed_late',
        daysRemaining: 0,
        deadline: deadline.toISOString()
      };
    }
    
    // If still open
    if (msRemaining <= 0) {
      return { status: 'overdue', daysRemaining, deadline: deadline.toISOString() };
    } else if (hoursRemaining <= 24) {
      return { status: 'critical_soon', daysRemaining, deadline: deadline.toISOString() };
    } else if (hoursRemaining <= 72) {
      return { status: 'warning', daysRemaining, deadline: deadline.toISOString() };
    }
    
    return { status: 'on_track', daysRemaining, deadline: deadline.toISOString() };
  };

  const getSlaStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'overdue': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'critical_soon': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'warning': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'on_track': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'fixed_on_time': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'fixed_late': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getSlaStatusLabel = (slaStatus, daysRemaining) => {
    switch (slaStatus) {
      case 'overdue': return `${Math.abs(daysRemaining)}d Overdue`;
      case 'critical_soon': return `<24h Left`;
      case 'warning': return `${daysRemaining}d Left`;
      case 'on_track': return `${daysRemaining}d Left`;
      case 'fixed_on_time': return 'Fixed On Time';
      case 'fixed_late': return 'Fixed Late';
      default: return 'Unknown';
    }
  };

  // Calculate SLA statistics
  const slaStats = {
    total: vulnerabilities.length,
    onTrack: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return ['on_track', 'warning'].includes(sla.status);
    }).length,
    overdue: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return sla.status === 'overdue';
    }).length,
    criticalSoon: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return sla.status === 'critical_soon';
    }).length,
    fixedOnTime: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return sla.status === 'fixed_on_time';
    }).length,
    fixedLate: vulnerabilities.filter(v => {
      const sla = calculateSlaStatus(v);
      return sla.status === 'fixed_late';
    }).length,
    missingRemediation: vulnerabilities.filter(v => 
      !v.assignee && v.status !== 'closed'
    ).length,
  };

  // SLA chart data
  const slaChartData = [
    { name: 'On Track', value: slaStats.onTrack, color: '#22c55e' },
    { name: 'Critical Soon', value: slaStats.criticalSoon, color: '#f97316' },
    { name: 'Overdue', value: slaStats.overdue, color: '#ef4444' },
    { name: 'Fixed On Time', value: slaStats.fixedOnTime, color: '#10b981' },
    { name: 'Fixed Late', value: slaStats.fixedLate, color: '#8b5cf6' },
  ].filter(d => d.value > 0);

  // Remediation status chart data
  const remediationChartData = [
    { name: 'Assigned', value: vulnerabilities.filter(v => v.assignee && v.status !== 'closed').length, color: '#3b82f6' },
    { name: 'Missing Remediation', value: slaStats.missingRemediation, color: '#f43f5e' },
    { name: 'Closed', value: vulnerabilities.filter(v => v.status === 'closed').length, color: '#22c55e' },
  ].filter(d => d.value > 0);

  // Export to CSV function
  const handleExportCSV = (type = 'list') => {
    let csvContent = '';
    let filename = '';
    
    const vulnsToExport = selectedVulns.size > 0 
      ? filteredVulnerabilities.filter(v => selectedVulns.has(v.id))
      : filteredVulnerabilities;
    
    if (type === 'list') {
      csvContent = 'ID,Title,CVE,Severity,CVSS Score,EPSS Score,Status,SLA Status,SLA Deadline,Assignee,Discovered,Source,Description\n';
      vulnsToExport.forEach(v => {
        const sla = calculateSlaStatus(v);
        csvContent += `"${v.id}","${v.title}","${v.cve_id || ''}","${v.severity}","${v.cvss_score || ''}","${v.epss_score || ''}","${v.status}","${getSlaStatusLabel(sla.status, sla.daysRemaining)}","${sla.deadline || ''}","${v.assignee || ''}","${new Date(v.discovered_at).toLocaleDateString()}","${v.source || 'manual'}","${(v.description || '').replace(/"/g, '""')}"\n`;
      });
      filename = `defendly-vulnerabilities-${new Date().toISOString().split('T')[0]}.csv`;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${vulnsToExport.length} vulnerabilities`);
  };

  // Handle status change
  const handleStatusChange = async (vulnId, newStatus) => {
    setUpdatingStatus(vulnId);
    try {
      await api.patch(`/vulnerabilities/${vulnId}/status`, { status: newStatus });
      
      setVulnerabilities(prev => 
        prev.map(v => v.id === vulnId ? { 
          ...v, 
          status: newStatus,
          closed_at: newStatus === 'closed' ? new Date().toISOString() : null
        } : v)
      );
      
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (selectedVulns.size === 0) {
      toast.error('Please select vulnerabilities first');
      return;
    }

    const vulnIds = Array.from(selectedVulns);
    
    if (action === 'mark_closed') {
      try {
        await Promise.all(vulnIds.map(id => 
          api.patch(`/vulnerabilities/${id}/status`, { status: 'closed' })
        ));
        setVulnerabilities(prev => 
          prev.map(v => selectedVulns.has(v.id) ? { ...v, status: 'closed', closed_at: new Date().toISOString() } : v)
        );
        toast.success(`Marked ${vulnIds.length} vulnerabilities as closed`);
        setSelectedVulns(new Set());
      } catch (error) {
        toast.error('Failed to update vulnerabilities');
      }
    } else if (action === 'mark_remediation') {
      try {
        await Promise.all(vulnIds.map(id => 
          api.patch(`/vulnerabilities/${id}/status`, { status: 'under_remediation' })
        ));
        setVulnerabilities(prev => 
          prev.map(v => selectedVulns.has(v.id) ? { ...v, status: 'under_remediation' } : v)
        );
        toast.success(`Marked ${vulnIds.length} vulnerabilities as under remediation`);
        setSelectedVulns(new Set());
      } catch (error) {
        toast.error('Failed to update vulnerabilities');
      }
    } else if (action === 'export') {
      handleExportCSV('list');
    } else if (action === 'assign') {
      setShowAssignDialog(true);
    }
  };

  // Assign vulnerabilities
  const handleAssign = async () => {
    if (!assignee) {
      toast.error('Please select an assignee');
      return;
    }

    const vulnIds = Array.from(selectedVulns);
    try {
      await Promise.all(vulnIds.map(id => 
        api.patch(`/vulnerabilities/${id}/assign`, { 
          assignee, 
          note: assignNote 
        }).catch(() => null)
      ));
      setVulnerabilities(prev => 
        prev.map(v => selectedVulns.has(v.id) ? { ...v, assignee, status: 'under_remediation' } : v)
      );
      toast.success(`Assigned ${vulnIds.length} vulnerabilities to ${assignee}`);
      setSelectedVulns(new Set());
      setShowAssignDialog(false);
      setAssignee('');
      setAssignNote('');
    } catch (error) {
      toast.error('Failed to assign vulnerabilities');
    }
  };

  // CVE Lookup
  const lookupCve = async (cveId) => {
    if (!cveId) return;
    setLoadingCve(true);
    setShowCveDialog(true);
    
    try {
      const response = await api.get(`/cve/lookup/${cveId}`).catch(() => null);
      if (response?.data) {
        setCveDetails(response.data);
      } else {
        setCveDetails({
          id: cveId,
          description: 'CVE details fetched from NVD database',
          severity: 'HIGH',
          cvss: 7.8,
          epss: 0.023,
          references: [
            { url: `https://nvd.nist.gov/vuln/detail/${cveId}`, source: 'NVD' },
          ],
          published: '2024-01-15',
          cwe: 'CWE-79',
        });
      }
    } catch (error) {
      toast.error('Failed to lookup CVE');
      setCveDetails(null);
    } finally {
      setLoadingCve(false);
    }
  };

  // Toggle selection
  const toggleVulnSelection = (vulnId) => {
    setSelectedVulns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vulnId)) {
        newSet.delete(vulnId);
      } else {
        newSet.add(vulnId);
      }
      return newSet;
    });
  };

  const selectAllFiltered = () => {
    if (selectedVulns.size === filteredVulnerabilities.length) {
      setSelectedVulns(new Set());
    } else {
      setSelectedVulns(new Set(filteredVulnerabilities.map(v => v.id)));
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return colors[severity] || colors.info;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-red-400',
      closed: 'text-green-400',
      under_remediation: 'text-yellow-400',
      approved_exception: 'text-blue-400',
    };
    return colors[status] || 'text-zinc-400';
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: 'Open',
      closed: 'Closed',
      under_remediation: 'Under Remediation',
      approved_exception: 'Approved Exception',
    };
    return labels[status] || status;
  };

  const getSourceLabel = (source) => {
    const labels = {
      // Backend AssessmentType enum values
      web: 'Web Application',
      api: 'API Security',
      mobile: 'Mobile App',
      infrastructure: 'Infrastructure',
      network: 'Network Scan',
      compliance: 'Compliance Check',
      secure_code: 'Secure Code Review',
      ai_pentest: 'AI Pentest',
      // Legacy/backward compatibility
      pentest: 'Penetration Test',
      raptor: 'Raptor AI',
      cloud: 'Cloud Security',
      manual: 'Manual Entry',
      code_scan: 'Code Scan',
      nmap: 'Nmap',
      nuclei: 'Nuclei',
    };
    return labels[source] || source || 'Unknown';
  };

  const toggleAssetExpand = (assetId) => {
    setExpandedAssets(prev => ({
      ...prev,
      [assetId]: !prev[assetId]
    }));
  };

  // Apply all filters including SLA and advanced filters
  const filteredVulnerabilities = vulnerabilities.filter((vuln) => {
    const matchesSearch = vuln.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vuln.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vuln.cve_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vuln.cwe?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || vuln.severity === severityFilter;
    const matchesStatus = statusFilter === 'all' || vuln.status === statusFilter;
    const matchesAsset = assetFilter === 'all' || vuln.asset_id === assetFilter;
    const matchesAssessment = assessmentFilter === 'all' || vuln.scan_id === assessmentFilter || vuln.assessment_id === assessmentFilter;
    
    // Assessment Type filter (combines source, assessment_type, scan_type)
    const vulnAssessmentType = vuln.assessment_type || vuln.scan_type || vuln.source;
    const matchesAssessmentType = assessmentTypeFilter === 'all' || vulnAssessmentType === assessmentTypeFilter;
    
    // Assessment Name filter
    const matchesAssessmentName = assessmentNameFilter === 'all' || vuln.assessment_name === assessmentNameFilter;
    
    // SLA filter
    let matchesSla = true;
    if (slaFilter !== 'all') {
      const slaStatus = calculateSlaStatus(vuln);
      if (slaFilter === 'overdue') matchesSla = slaStatus.status === 'overdue';
      else if (slaFilter === 'on_track') matchesSla = ['on_track', 'warning'].includes(slaStatus.status);
      else if (slaFilter === 'critical') matchesSla = slaStatus.status === 'critical_soon';
    }
    
    // Advanced filters
    const cvss = vuln.cvss_score || 0;
    const matchesCvss = cvss >= cvssRange[0] && cvss <= cvssRange[1];
    
    const epss = (vuln.epss_score || 0) * 100;
    const matchesEpss = epss >= epssRange[0] && epss <= epssRange[1];
    
    const matchesAssignee = assigneeFilter === 'all' || vuln.assignee === assigneeFilter;
    
    // Discovery date filter
    let matchesDate = true;
    if (dateRange.from) {
      const vulnDate = new Date(vuln.discovered_at);
      const fromDate = new Date(dateRange.from);
      if (vulnDate < fromDate) matchesDate = false;
    }
    if (dateRange.to) {
      const vulnDate = new Date(vuln.discovered_at);
      const toDate = new Date(dateRange.to);
      if (vulnDate > toDate) matchesDate = false;
    }
    
    // Assessment Start Date filter
    let matchesAssessmentStartDate = true;
    if (assessmentStartDateRange.from) {
      const assessmentDate = new Date(vuln.scan_started_at || vuln.created_at);
      const fromDate = new Date(assessmentStartDateRange.from);
      if (assessmentDate < fromDate) matchesAssessmentStartDate = false;
    }
    if (assessmentStartDateRange.to) {
      const assessmentDate = new Date(vuln.scan_started_at || vuln.created_at);
      const toDate = new Date(assessmentStartDateRange.to);
      if (assessmentDate > toDate) matchesAssessmentStartDate = false;
    }
    
    // Assessment Complete Date filter
    let matchesAssessmentCompleteDate = true;
    if (assessmentCompleteDateRange.from) {
      const completeDate = new Date(vuln.scan_completed_at || vuln.discovered_at);
      const fromDate = new Date(assessmentCompleteDateRange.from);
      if (completeDate < fromDate) matchesAssessmentCompleteDate = false;
    }
    if (assessmentCompleteDateRange.to) {
      const completeDate = new Date(vuln.scan_completed_at || vuln.discovered_at);
      const toDate = new Date(assessmentCompleteDateRange.to);
      if (completeDate > toDate) matchesAssessmentCompleteDate = false;
    }
    
    const matchesCwe = !cweFilter || vuln.cwe?.toLowerCase().includes(cweFilter.toLowerCase());
    
    const matchesExploited = exploitedFilter === 'all' || 
      (exploitedFilter === 'yes' && vuln.is_exploited) || 
      (exploitedFilter === 'no' && !vuln.is_exploited);
    
    // Asset type filter - get asset type from the asset
    const assetInfo = assets.find(a => a.id === vuln.asset_id);
    const matchesAssetType = assetTypeFilter === 'all' || assetInfo?.type === assetTypeFilter;
    
    // Tags filter
    const matchesTags = !tagsFilter || vuln.tags?.some(tag => 
      tag.toLowerCase().includes(tagsFilter.toLowerCase())
    );
    
    // Priority filter
    const matchesPriority = priorityFilter === 'all' || vuln.priority === priorityFilter;
    
    // Compliance filter
    const matchesCompliance = complianceFilter === 'all' || 
      vuln.compliance_frameworks?.includes(complianceFilter) ||
      vuln.compliance?.includes(complianceFilter);
    
    return matchesSearch && matchesSeverity && matchesStatus && matchesAsset && 
           matchesAssessment && matchesAssessmentType && matchesAssessmentName && matchesSla &&
           matchesCvss && matchesEpss && matchesAssignee && matchesDate && 
           matchesAssessmentStartDate && matchesAssessmentCompleteDate &&
           matchesCwe && matchesExploited && matchesTags && 
           matchesPriority && matchesCompliance;
  });
  
  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    setCvssRange([0, 10]);
    setEpssRange([0, 100]);
    setDateRange({ from: '', to: '' });
    setAssigneeFilter('all');
    setCweFilter('');
    setExploitedFilter('all');
    setAssetTypeFilter('all');
    setAssessmentFilter('all');
    setAssessmentTypeFilter('all');
    setAssessmentNameFilter('all');
    setAssessmentStartDateRange({ from: '', to: '' });
    setAssessmentCompleteDateRange({ from: '', to: '' });
    setTagsFilter('');
    setPriorityFilter('all');
    setComplianceFilter('all');
  };
  
  // Clear all filters including basic ones
  const clearAllFilters = () => {
    setSearchTerm('');
    setSeverityFilter('all');
    setStatusFilter('all');
    setSourceFilter('all');
    setAssetFilter('all');
    setSlaFilter('all');
    clearAdvancedFilters();
  };
  
  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (severityFilter !== 'all') count++;
    if (statusFilter !== 'all') count++;
    if (sourceFilter !== 'all') count++;
    if (assetFilter !== 'all') count++;
    if (assessmentFilter !== 'all') count++;
    if (assessmentTypeFilter !== 'all') count++;
    if (assessmentNameFilter !== 'all') count++;
    if (slaFilter !== 'all') count++;
    if (cvssRange[0] !== 0 || cvssRange[1] !== 10) count++;
    if (epssRange[0] !== 0 || epssRange[1] !== 100) count++;
    if (dateRange.from || dateRange.to) count++;
    if (assessmentStartDateRange.from || assessmentStartDateRange.to) count++;
    if (assessmentCompleteDateRange.from || assessmentCompleteDateRange.to) count++;
    if (assigneeFilter !== 'all') count++;
    if (cweFilter) count++;
    if (exploitedFilter !== 'all') count++;
    if (assetTypeFilter !== 'all') count++;
    if (tagsFilter) count++;
    if (priorityFilter !== 'all') count++;
    if (complianceFilter !== 'all') count++;
    return count;
  };

  // Filter for By Asset tab
  const filteredByAsset = vulnerabilitiesByAsset.filter(item => {
    const matchesSearch = item.asset.name?.toLowerCase().includes(assetTabSearch.toLowerCase()) ||
                         item.asset.ip_address?.toLowerCase().includes(assetTabSearch.toLowerCase());
    return matchesSearch;
  });

  // Filter for By Source tab - Group by source type (SecureProbe, Code Analysis, SBOM)
  const getSourceCategory = (item) => {
    const source = item.source || item.assessment_type || '';
    const assessmentName = item.assessment_name || '';
    
    if (source === 'secure_code_analysis' || source.includes('code')) return 'Code Analysis';
    if (source === 'sbom' || source.includes('sbom') || source.includes('SBOM')) return 'SBOM Analysis';
    if (source === 'secureprobe' || source.includes('scan') || assessmentName) return 'SecureProbe Scans';
    return 'Other Scans';
  };
  
  // Group vulnerabilities by source category
  const groupedBySourceCategory = vulnerabilitiesByAssessment.reduce((acc, item) => {
    const category = getSourceCategory(item);
    if (!acc[category]) {
      acc[category] = {
        category,
        items: [],
        vulnerability_count: 0,
        critical_count: 0,
        high_count: 0
      };
    }
    acc[category].items.push(item);
    acc[category].vulnerability_count += item.vulnerability_count || 0;
    acc[category].critical_count += item.critical_count || 0;
    acc[category].high_count += item.high_count || 0;
    return acc;
  }, {});
  
  const filteredBySource = Object.values(groupedBySourceCategory).filter(group => {
    const matchesSearch = group.category.toLowerCase().includes(sourceTabSearch.toLowerCase()) ||
      group.items.some(item => (item.assessment_name || item.assessment_type || '').toLowerCase().includes(sourceTabSearch.toLowerCase()));
    return matchesSearch;
  });

  const stats = {
    total: vulnerabilities.length,
    critical: vulnerabilities.filter(v => v.severity === 'critical').length,
    high: vulnerabilities.filter(v => v.severity === 'high').length,
    medium: vulnerabilities.filter(v => v.severity === 'medium').length,
    low: vulnerabilities.filter(v => v.severity === 'low').length,
    open: vulnerabilities.filter(v => v.status === 'open').length,
    closed: vulnerabilities.filter(v => v.status === 'closed').length,
    remediation: vulnerabilities.filter(v => v.status === 'under_remediation').length,
  };

  const pieData = [
    { name: 'Critical', value: stats.critical, color: '#ef4444' },
    { name: 'High', value: stats.high, color: '#f97316' },
    { name: 'Medium', value: stats.medium, color: '#eab308' },
    { name: 'Low', value: stats.low, color: '#22c55e' },
  ].filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading vulnerabilities...</div>
      </div>
    );
  }

  // FilterBar component props object for cleaner JSX
  const filterBarProps = {
    isDarkMode,
    searchTerm,
    setSearchTerm,
    showAdvancedSearch,
    setShowAdvancedSearch,
    getActiveFilterCount,
    clearAllFilters,
    severityFilter,
    setSeverityFilter,
    SEVERITY_OPTIONS,
    statusFilter,
    setStatusFilter,
    STATUS_OPTIONS,
    assessmentTypeFilter,
    setAssessmentTypeFilter,
    ASSESSMENT_TYPES,
    assessmentNameFilter,
    setAssessmentNameFilter,
    assessmentNames,
    slaFilter,
    setSlaFilter,
    assetFilter,
    setAssetFilter,
    assets,
    assigneeFilter,
    setAssigneeFilter,
    users,
    priorityFilter,
    setPriorityFilter,
    exploitedFilter,
    setExploitedFilter,
    complianceFilter,
    setComplianceFilter,
    COMPLIANCE_OPTIONS,
    cweFilter,
    setCweFilter,
    dateRange,
    setDateRange,
    assessmentStartDateRange,
    setAssessmentStartDateRange,
    assessmentCompleteDateRange,
    setAssessmentCompleteDateRange,
    cvssRange,
    setCvssRange,
    epssRange,
    setEpssRange,
    tagsFilter,
    setTagsFilter,
    clearAdvancedFilters,
  };
  
  // REMOVED: Inline FilterBar definition - now extracted outside component to fix focus loss

  // Vulnerability card component with SLA and all fields
  const VulnerabilityCard = ({ vuln, showCheckbox = false }) => {
    const sla = calculateSlaStatus(vuln);
    const assetInfo = assets.find(a => a.id === vuln.asset_id);
    const assessmentType = vuln.assessment_type || vuln.scan_type || vuln.source;
    const assessmentDisplayName = vuln.assessment_name || getAssessmentTypeLabel(assessmentType);
    
    // Navigate to detail page
    const handleCardClick = (e) => {
      // Don't navigate if clicking on buttons or checkboxes
      if (e.target.closest('button') || e.target.closest('input[type="checkbox"]') || e.target.closest('[role="menuitem"]')) {
        return;
      }
      navigate(`/vulnerability/${vuln.id}`);
    };
    
    return (
      <div
        data-testid={`vulnerability-item-${vuln.id}`}
        onClick={handleCardClick}
        className={`p-4 lg:p-5 rounded-xl border transition-all cursor-pointer ${isDarkMode 
          ? `glass-hover border-white/10 hover:border-indigo-500/30 ${selectedVulns.has(vuln.id) ? 'bg-indigo-500/10 border-indigo-500/30' : ''}` 
          : `bg-white border-slate-200 hover:border-indigo-300 hover:shadow-md ${selectedVulns.has(vuln.id) ? 'bg-indigo-50 border-indigo-300' : ''}`}`}
      >
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-start gap-3">
              {showCheckbox && (
                <Checkbox 
                  checked={selectedVulns.has(vuln.id)}
                  onCheckedChange={() => toggleVulnSelection(vuln.id)}
                  className={`mt-1 ${isDarkMode ? 'border-white/20' : 'border-slate-300'}`}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <div className="flex-1">
                {/* Primary Badges Row */}
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {/* Severity */}
                  <span className={`px-2 py-0.5 rounded text-xs font-mono uppercase border ${getSeverityColor(vuln.severity)}`}>
                    {vuln.severity}
                  </span>
                  {/* Status */}
                  <span className={`px-2 py-0.5 rounded text-xs border ${
                    vuln.status === 'open' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                    vuln.status === 'closed' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                    'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                  }`}>
                    {getStatusLabel(vuln.status)}
                  </span>
                  {/* Assessment Name */}
                  {assessmentDisplayName && (
                    <span className={`px-2 py-0.5 rounded text-xs border flex items-center gap-1 ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' : 'bg-indigo-100 text-indigo-700 border-indigo-200'}`}>
                      <Clipboard className="w-3 h-3" />
                      {assessmentDisplayName}
                    </span>
                  )}
                  {/* SLA Badge */}
                  <span className={`px-2 py-0.5 rounded text-xs border flex items-center gap-1 ${getSlaStatusColor(sla.status)}`}>
                    {sla.status === 'overdue' ? <AlertCircle className="w-3 h-3" /> : <Timer className="w-3 h-3" />}
                    {getSlaStatusLabel(sla.status, sla.daysRemaining)}
                  </span>
                  {/* CVE */}
                  {vuln.cve_id && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); lookupCve(vuln.cve_id); }}
                      className="text-cyan-500 text-xs font-mono hover:underline flex items-center gap-1"
                    >
                      {vuln.cve_id}
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  )}
                  {/* CWE */}
                  {vuln.cwe && (
                    <span className={`px-2 py-0.5 rounded text-xs font-mono ${isDarkMode ? 'text-purple-400 bg-purple-500/20' : 'text-purple-600 bg-purple-100'}`}>
                      {vuln.cwe}
                    </span>
                  )}
                </div>
                
                {/* Title */}
                <h3 className={`font-rajdhani font-semibold text-lg lg:text-xl mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {vuln.title}
                </h3>
                
                {/* Description */}
                <p className={`font-inter text-sm mb-3 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{vuln.description}</p>
                
                {/* Secondary Info Row */}
                <div className={`flex flex-wrap items-center gap-3 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  {/* Discovery Date */}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(vuln.discovered_at).toLocaleDateString()}
                  </span>
                  {/* CVSS Score */}
                  {vuln.cvss_score && (
                    <span className={`font-mono px-1.5 py-0.5 rounded ${
                      vuln.cvss_score >= 9 ? 'bg-red-500/20 text-red-400' :
                      vuln.cvss_score >= 7 ? 'bg-orange-500/20 text-orange-400' :
                      vuln.cvss_score >= 4 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      CVSS: {vuln.cvss_score}
                    </span>
                  )}
                  {/* EPSS Score */}
                  {vuln.epss_score && (
                    <span className={`font-mono px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700'}`}>
                      EPSS: {(vuln.epss_score * 100).toFixed(1)}%
                    </span>
                  )}
                  {/* Risk Score */}
                  {vuln.risk_score && (
                    <span className={`font-mono px-1.5 py-0.5 rounded flex items-center gap-1 ${
                      getRiskLevel(vuln.risk_score) === 'critical' ? 'bg-red-500/20 text-red-400' :
                      getRiskLevel(vuln.risk_score) === 'high' ? 'bg-orange-500/20 text-orange-400' :
                      getRiskLevel(vuln.risk_score) === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      <Target className="w-3 h-3" />
                      Risk: {vuln.risk_score?.toFixed(1)}
                    </span>
                  )}
                  {/* ALE */}
                  {(vuln.ale || vuln.ale_data?.ale || perVulnAle[vuln.id]) && (
                    <span className={`font-mono px-1.5 py-0.5 rounded flex items-center gap-1 ${isDarkMode ? 'bg-amber-600/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                      <DollarSign className="w-3 h-3" />
                      {formatCurrency(perVulnAle[vuln.id]?.ale || vuln.ale || vuln.ale_data?.ale)}
                    </span>
                  )}
                  {/* Source */}
                  <span className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'text-zinc-500 bg-zinc-800' : 'text-slate-600 bg-slate-100'}`}>
                    {getSourceLabel(vuln.source)}
                  </span>
                </div>
                
                {/* Tertiary Info Row - Asset, Assignee, Tags, etc */}
                <div className={`flex flex-wrap items-center gap-3 text-xs mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  {/* Asset Info */}
                  {(vuln.asset_name || assetInfo) && (
                    <span className="flex items-center gap-1">
                      <Server className="w-3 h-3" />
                      {vuln.asset_name || assetInfo?.name}
                      {(vuln.asset_type || assetInfo?.type) && (
                        <span className={`ml-1 px-1 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
                          {getAssetTypeLabel(vuln.asset_type || assetInfo?.type)}
                        </span>
                      )}
                    </span>
                  )}
                  {/* Assignee */}
                  {vuln.assignee && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {vuln.assignee}
                    </span>
                  )}
                  {/* Priority */}
                  {vuln.priority && (
                    <span className={`px-1.5 py-0.5 rounded ${
                      vuln.priority === 'P0' || vuln.priority === 'P1' ? 'bg-red-500/20 text-red-400' :
                      vuln.priority === 'P2' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {vuln.priority}
                    </span>
                  )}
                  {/* Known Exploited */}
                  {vuln.is_exploited && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                      <AlertTriangle className="w-3 h-3" />
                      KEV
                    </span>
                  )}
                  {/* Tags */}
                  {vuln.tags && vuln.tags.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {vuln.tags.slice(0, 2).map((tag, i) => (
                        <span key={i} className={`px-1 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
                          {tag}
                        </span>
                      ))}
                      {vuln.tags.length > 2 && <span>+{vuln.tags.length - 2}</span>}
                    </span>
                  )}
                  {/* Compliance */}
                  {vuln.compliance_frameworks && vuln.compliance_frameworks.length > 0 && (
                    <span className="flex items-center gap-1">
                      <FileWarning className="w-3 h-3" />
                      {vuln.compliance_frameworks.slice(0, 1).join(', ')}
                      {vuln.compliance_frameworks.length > 1 && <span>+{vuln.compliance_frameworks.length - 1}</span>}
                    </span>
                  )}
                  {/* Status Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className={`h-5 px-2 ${getStatusColor(vuln.status)}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Change Status
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                      {['open', 'under_remediation', 'closed'].map(status => (
                        <DropdownMenuItem 
                          key={status}
                          onClick={(e) => { e.stopPropagation(); handleStatusChange(vuln.id, status); }}
                          className="cursor-pointer"
                        >
                          {vuln.status === status && <Check className="w-3 h-3 mr-2" />}
                          {getStatusLabel(status)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-row lg:flex-col gap-2">
            <Button
              onClick={(e) => { e.stopPropagation(); navigate(`/vulnerability/${vuln.id}`); }}
              className="bg-indigo-600 hover:bg-indigo-500 font-rajdhani text-xs px-3 py-2 flex-1 lg:flex-none"
              size="sm"
            >
              <Eye className="w-3 h-3 mr-1" />
              Details
            </Button>
            <Button
              onClick={(e) => { e.stopPropagation(); navigate(`/ai-insights/${vuln.id}`); }}
              className="bg-violet-600 hover:bg-violet-500 font-rajdhani text-xs px-3 py-2 flex-1 lg:flex-none"
              size="sm"
            >
              <Sparkles className="w-3 h-3 mr-1" />
              Insights
            </Button>
            <Button
              onClick={(e) => { e.stopPropagation(); navigate(`/ai-remediation/${vuln.id}`); }}
              className="bg-emerald-600 hover:bg-emerald-500 font-rajdhani text-xs px-3 py-2 flex-1 lg:flex-none"
              size="sm"
            >
              <Shield className="w-3 h-3 mr-1" />
              Remediate
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="vulnerability-hub-container" className="space-y-5">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-red-500/15 border border-red-500/30`}>
            <Shield className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h1 className={`font-inter font-bold text-xl lg:text-2xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Vulnerability Hub
            </h1>
            <p className={`font-inter text-sm mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>AI-powered vulnerability analysis, tracking, and remediation</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleCalculateAle}
            disabled={calculatingAle}
            size="sm"
            className="bg-violet-600 hover:bg-violet-500"
            data-testid="calculate-ale-btn"
          >
            {calculatingAle ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
            Calculate ALE
          </Button>
          <Button
            onClick={() => setShowSlaSettings(true)}
            variant="outline"
            size="sm"
            className="border-navy-700 text-black-300 hover:bg-navy-800 hover:border-electric-500/50"
          >
            <Settings className="w-4 h-4 mr-2" />
            SLA Settings
          </Button>
          <Button
            onClick={fetchVulnerabilities}
            variant="outline"
            size="sm"
            className="border-navy-700 text-black-300 hover:bg-navy-800 hover:border-electric-500/50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Assessment Filter Banner */}
      {assessmentFilter !== 'all' && (
        <div className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-indigo-50 border border-indigo-200'}`}>
          <div className="flex items-center gap-2">
            <Filter className={`w-4 h-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <span className={`text-sm font-medium ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
              Filtered by Assessment: <strong>{filteredVulnerabilities.length}</strong> vulnerabilities found
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAssessmentFilter('all')}
            className={`${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
            data-testid="clear-assessment-filter"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Filter
          </Button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white-800 dark:bg-navy-800/50 border border-navy-700 flex flex-wrap h-auto p-1">
          <TabsTrigger 
            value="overview" 
            className="data-[state=active]:bg-electric-500 data-[state=active]:text-navy-950 text-xs lg:text-sm"
          >
            <LayoutGrid className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger 
            value="list" 
            className="data-[state=active]:bg-electric-500 data-[state=active]:text-navy-950 text-xs lg:text-sm"
          >
            <Table className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">List</span>
          </TabsTrigger>
          <TabsTrigger 
            value="by-asset" 
            className="data-[state=active]:bg-emerald-500 data-[state=active]:text-navy-950 text-xs lg:text-sm"
          >
            <Server className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">By Asset</span>
          </TabsTrigger>
          <TabsTrigger 
            value="by-assessment" 
            className="data-[state=active]:bg-violet-500 data-[state=active]:text-white text-xs lg:text-sm"
          >
            <ClipboardList className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">By Source</span>
          </TabsTrigger>
          <TabsTrigger 
            value="remediation" 
            className="data-[state=active]:bg-rose-500 data-[state=active]:text-white text-xs lg:text-sm"
          >
            <Zap className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Remediation</span>
          </TabsTrigger>
          <TabsTrigger 
            value="correlation" 
            className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white text-xs lg:text-sm"
          >
            <GitBranch className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Correlation</span>
          </TabsTrigger>
          <TabsTrigger 
            value="trends" 
            className="data-[state=active]:bg-sky-500 data-[state=active]:text-navy-950 text-xs lg:text-sm"
          >
            <BarChart3 className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Trends</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-5">
          {/* Stats Cards - Responsive Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 lg:gap-3 mb-5">
            <div className={`rounded-xl p-3 lg:p-4 text-center cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border border-navy-700 hover:border-electric-500/50' : 'bg-white border border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setSeverityFilter('all'); setActiveTab('list'); }}>
              <p className={`text-xl lg:text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stats.total}</p>
              <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Total</p>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 text-center border-l-2 border-red-500 cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border-t border-r border-b border-navy-700 hover:border-red-400' : 'bg-white border-t border-r border-b border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setSeverityFilter('critical'); setActiveTab('list'); }}>
              <p className="text-xl lg:text-2xl font-inter font-bold text-red-500">{stats.critical}</p>
              <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Critical</p>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 text-center border-l-2 border-orange-500 cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border-t border-r border-b border-navy-700 hover:border-orange-400' : 'bg-white border-t border-r border-b border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setSeverityFilter('high'); setActiveTab('list'); }}>
              <p className="text-xl lg:text-2xl font-inter font-bold text-orange-500">{stats.high}</p>
              <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>High</p>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 text-center border-l-2 border-yellow-500 cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border-t border-r border-b border-navy-700 hover:border-yellow-400' : 'bg-white border-t border-r border-b border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setSeverityFilter('medium'); setActiveTab('list'); }}>
              <p className="text-xl lg:text-2xl font-inter font-bold text-yellow-500">{stats.medium}</p>
              <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Medium</p>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 text-center border-l-2 border-green-500 cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border-t border-r border-b border-navy-700 hover:border-green-400' : 'bg-white border-t border-r border-b border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setSeverityFilter('low'); setActiveTab('list'); }}>
              <p className="text-xl lg:text-2xl font-inter font-bold text-green-500">{stats.low}</p>
              <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Low</p>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 text-center border-l-2 border-red-400 cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border-t border-r border-b border-navy-700 hover:border-red-300' : 'bg-white border-t border-r border-b border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setStatusFilter('open'); setActiveTab('list'); }}>
              <p className={`text-xl lg:text-2xl font-inter font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{stats.open}</p>
              <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Open</p>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 text-center border-l-2 border-yellow-400 cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border-t border-r border-b border-navy-700 hover:border-yellow-300' : 'bg-white border-t border-r border-b border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setStatusFilter('under_remediation'); setActiveTab('list'); }}>
              <p className={`text-xl lg:text-2xl font-inter font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{stats.remediation}</p>
              <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>In Progress</p>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 text-center border-l-2 border-green-400 cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border-t border-r border-b border-navy-700 hover:border-green-300' : 'bg-white border-t border-r border-b border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setStatusFilter('closed'); setActiveTab('list'); }}>
              <p className={`text-xl lg:text-2xl font-inter font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{stats.closed}</p>
              <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Closed</p>
            </div>
          </div>

          {/* SLA Status Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3 mb-5">
            <div className={`rounded-xl p-3 lg:p-4 border-l-2 border-green-500 cursor-pointer transition-colors ${isDarkMode ? 'bg-navy-800/50 border-t border-r border-b border-navy-700 hover:bg-navy-800' : 'bg-white border-t border-r border-b border-slate-200 shadow-card hover:shadow-card-hover'}`} onClick={() => { setSlaFilter('on_track'); setActiveTab('list'); }}>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <div>
                  <p className="text-lg lg:text-xl font-rajdhani font-bold text-green-400">{slaStats.onTrack}</p>
                  <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>On Track</p>
                </div>
              </div>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 border-l-2 border-orange-500 cursor-pointer transition-colors ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 hover:bg-slate-50'}`} onClick={() => { setSlaFilter('critical'); setActiveTab('list'); }}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                <div>
                  <p className="text-lg lg:text-xl font-rajdhani font-bold text-orange-400">{slaStats.criticalSoon}</p>
                  <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Critical Soon</p>
                </div>
              </div>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 border-l-2 border-red-500 cursor-pointer transition-colors ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 hover:bg-slate-50'}`} onClick={() => { setSlaFilter('overdue'); setActiveTab('list'); }}>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <div>
                  <p className="text-lg lg:text-xl font-rajdhani font-bold text-red-400">{slaStats.overdue}</p>
                  <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Overdue</p>
                </div>
              </div>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 border-l-2 border-rose-500 cursor-pointer transition-colors ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 hover:bg-slate-50'}`} onClick={() => { setAssetFilter('all'); setStatusFilter('open'); setActiveTab('list'); }}>
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-rose-400" />
                <div>
                  <p className="text-lg lg:text-xl font-rajdhani font-bold text-rose-400">{slaStats.missingRemediation}</p>
                  <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Missing Remediation</p>
                </div>
              </div>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 border-l-2 border-emerald-500 cursor-pointer transition-colors ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 hover:bg-slate-50'}`}>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <div>
                  <p className="text-lg lg:text-xl font-rajdhani font-bold text-emerald-400">{slaStats.fixedOnTime}</p>
                  <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Fixed On Time</p>
                </div>
              </div>
            </div>
            <div className={`rounded-xl p-3 lg:p-4 border-l-2 border-purple-500 cursor-pointer transition-colors ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 hover:bg-slate-50'}`}>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-purple-400" />
                <div>
                  <p className="text-lg lg:text-xl font-rajdhani font-bold text-purple-400">{slaStats.fixedLate}</p>
                  <p className={`text-[10px] lg:text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Fixed Late</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Enterprise Metrics Row - Remediation Tracking & Risk Score */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            {/* Remediation Tracking Panel */}
            <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass border border-cyan-500/20' : 'bg-white border border-cyan-200 shadow-card'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                  <Activity className="w-5 h-5 text-cyan-500" />
                </div>
                <div>
                  <h3 className={`font-rajdhani font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Remediation Tracking
                  </h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Performance metrics for vulnerability management
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {/* MTTR */}
                <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-cyan-50 border border-cyan-100'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>MTTR</span>
                    <span className={`text-xs flex items-center ${remediationStats.mttrTrend < 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {remediationStats.mttrTrend < 0 ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                      {Math.abs(remediationStats.mttrTrend)}%
                    </span>
                  </div>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{remediationStats.mttr} days</p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Mean Time to Remediate</p>
                </div>
                
                {/* Fix Rate */}
                <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Fix Rate</span>
                    <span className={`text-xs flex items-center ${remediationStats.fixRateTrend > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {remediationStats.fixRateTrend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {Math.abs(remediationStats.fixRateTrend)}%
                    </span>
                  </div>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{remediationStats.fixRate}%</p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Vulnerabilities Resolved</p>
                </div>
                
                {/* In Progress */}
                <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-100'}`}>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>In Progress</span>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{remediationStats.inProgress}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Active remediations</p>
                </div>
                
                {/* Pending Review */}
                <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'}`}>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-violet-400' : 'text-violet-700'}`}>Pending Review</span>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{remediationStats.pendingReview}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Awaiting verification</p>
                </div>
              </div>
            </div>
            
            {/* Risk Score Visualization */}
            <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass border border-rose-500/20' : 'bg-white border border-rose-200 shadow-card'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-rose-500/20' : 'bg-rose-100'}`}>
                  <Gauge className="w-5 h-5 text-rose-500" />
                </div>
                <div>
                  <h3 className={`font-rajdhani font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Risk Score Distribution
                  </h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Vulnerability risk assessment breakdown
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="h-36 flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart 
                      cx="50%" 
                      cy="50%" 
                      innerRadius="30%" 
                      outerRadius="100%" 
                      data={riskDistribution}
                      startAngle={180}
                      endAngle={0}
                    >
                      <RadialBar 
                        minAngle={15} 
                        background 
                        clockWise 
                        dataKey="value" 
                        cornerRadius={4}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {riskDistribution.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }} />
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{item.name}</span>
                      <span className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* FAIR Model - Annual Loss Expectancy (ALE) Section */}
          {aleMetrics && (
            <div className={`rounded-2xl p-4 lg:p-5 mb-5 ${isDarkMode ? 'glass border border-violet-500/20' : 'bg-white border border-violet-200 shadow-card'}`}>
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                    <DollarSign className="w-5 h-5 text-violet-500" />
                  </div>
                  <div>
                    <h3 className={`font-rajdhani font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      FAIR Model - Annual Loss Expectancy
                    </h3>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      Risk quantification based on breach cost × probability
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className={`mt-2 lg:mt-0 ${isDarkMode ? 'border-violet-500/50 text-violet-400' : 'border-violet-300 text-violet-600'}`}>
                  <Zap className="w-3 h-3 mr-1" />
                  FAIR Framework
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {/* Total Current Exposure */}
                <div className={`rounded-xl p-3 lg:p-4 ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-100'}`}>
                  <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Current Exposure</p>
                  <p className={`text-xl lg:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {aleMetrics.current_exposure?.total_ale_formatted || '$0'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {aleMetrics.current_exposure?.open_vulnerabilities || 0} vulnerabilities
                  </p>
                </div>
                
                {/* Savings from Remediation */}
                <div className={`rounded-xl p-3 lg:p-4 ${isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-100'}`}>
                  <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Savings (90 days)</p>
                  <p className={`text-xl lg:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {aleMetrics.risk_reduction?.savings_formatted || '$0'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {aleMetrics.risk_reduction?.closed_vulnerabilities || 0} closed
                  </p>
                </div>
                
                {/* Risk Reduction % */}
                <div className={`rounded-xl p-3 lg:p-4 ${isDarkMode ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'}`}>
                  <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>Risk Reduction</p>
                  <p className={`text-xl lg:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {aleMetrics.risk_reduction?.risk_reduction_percentage?.toFixed(1) || 0}%
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    of potential loss avoided
                  </p>
                </div>
                
                {/* Potential Loss if Unpatched */}
                <div className={`rounded-xl p-3 lg:p-4 ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-orange-50 border border-orange-100'}`}>
                  <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>Potential Loss</p>
                  <p className={`text-xl lg:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {aleMetrics.risk_reduction?.potential_ale_formatted || '$0'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    if nothing fixed
                  </p>
                </div>
              </div>
              
              {/* ALE by Severity */}
              {aleMetrics.current_exposure?.by_severity_formatted && (
                <div className="mt-4 grid grid-cols-4 gap-2">
                  <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Critical</p>
                    <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {aleMetrics.current_exposure.by_severity_formatted.critical}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-orange-500/10' : 'bg-orange-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>High</p>
                    <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {aleMetrics.current_exposure.by_severity_formatted.high}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-yellow-500/10' : 'bg-yellow-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Medium</p>
                    <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {aleMetrics.current_exposure.by_severity_formatted.medium}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-green-500/10' : 'bg-green-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Low</p>
                    <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {aleMetrics.current_exposure.by_severity_formatted.low}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Charts - Responsive Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
            <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
              <h3 className={`font-rajdhani font-semibold text-base lg:text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Severity Distribution</h3>
              <div className="h-40 lg:h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#ffffff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', color: isDarkMode ? '#fff' : '#1e293b' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                {pieData.map((entry) => (
                  <div key={entry.name} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
              <h3 className={`font-rajdhani font-semibold text-base lg:text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>SLA Compliance</h3>
              <div className="h-40 lg:h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={slaChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {slaChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#ffffff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', color: isDarkMode ? '#fff' : '#1e293b' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                {slaChartData.map((entry) => (
                  <div key={entry.name} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
              <h3 className={`font-rajdhani font-semibold text-base lg:text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Remediation Status</h3>
              <div className="h-40 lg:h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={remediationChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {remediationChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#ffffff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', color: isDarkMode ? '#fff' : '#1e293b' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                {remediationChartData.map((entry) => (
                  <div key={entry.name} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Filters and List */}
          <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
            <FilterBar {...filterBarProps} />

            <div className="space-y-3">
              {filteredVulnerabilities.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                  <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No vulnerabilities found</p>
                </div>
              ) : (
                filteredVulnerabilities.slice(0, 10).map((vuln) => (
                  <VulnerabilityCard key={vuln.id} vuln={vuln} />
                ))
              )}
              {filteredVulnerabilities.length > 10 && (
                <div className="text-center py-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveTab('list')}
                    className={isDarkMode ? 'border-white/10 text-zinc-300' : 'border-slate-300 text-slate-700'}
                  >
                    View All {filteredVulnerabilities.length} Vulnerabilities
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* List Tab */}
        <TabsContent value="list" className="mt-5">
          {/* Advanced Search Panel */}
          <Collapsible open={showAdvancedSearch} onOpenChange={setShowAdvancedSearch} className="mb-4">
            <div className={`rounded-2xl p-4 ${isDarkMode ? 'glass border border-indigo-500/20' : 'bg-white border border-indigo-200 shadow-sm'}`}>
              <CollapsibleTrigger asChild>
                <Button 
                  variant="ghost" 
                  className={`w-full flex items-center justify-between p-0 h-auto hover:bg-transparent ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                  data-testid="advanced-search-toggle"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                      <SlidersHorizontal className="w-4 h-4 text-indigo-500" />
                    </div>
                    <div className="text-left">
                      <span className="font-rajdhani font-semibold">Advanced Search</span>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        Filter by CVSS, EPSS, date range, assignee, and more
                      </p>
                    </div>
                  </div>
                  {showAdvancedSearch ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* CVSS Score Range */}
                  <div>
                    <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      CVSS Score: {cvssRange[0]} - {cvssRange[1]}
                    </Label>
                    <Slider
                      value={cvssRange}
                      onValueChange={setCvssRange}
                      max={10}
                      step={0.1}
                      className="mt-2"
                      data-testid="cvss-range-slider"
                    />
                  </div>
                  
                  {/* EPSS Score Range */}
                  <div>
                    <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      EPSS Score: {epssRange[0]}% - {epssRange[1]}%
                    </Label>
                    <Slider
                      value={epssRange}
                      onValueChange={setEpssRange}
                      max={100}
                      step={1}
                      className="mt-2"
                      data-testid="epss-range-slider"
                    />
                  </div>
                  
                  {/* Assignee Filter */}
                  <div>
                    <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Assignee</Label>
                    <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                      <SelectTrigger className={`${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
                        <SelectValue placeholder="All assignees" />
                      </SelectTrigger>
                      <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                        <SelectItem value="all">All Assignees</SelectItem>
                        {/* Use users if available, otherwise use unique assignees from vulnerabilities */}
                        {(users.length > 0 ? users.map(user => user.email) : uniqueAssignees)
                          .filter(Boolean)
                          .map(email => (
                            <SelectItem key={email} value={email}>{email}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Exploited Filter */}
                  <div>
                    <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Known Exploited</Label>
                    <Select value={exploitedFilter} onValueChange={setExploitedFilter}>
                      <SelectTrigger className={`${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="yes">Exploited (KEV)</SelectItem>
                        <SelectItem value="no">Not Exploited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date From */}
                  <div>
                    <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Discovered From</Label>
                    <Input
                      type="date"
                      value={dateRange.from}
                      onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                      className={`${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                      data-testid="date-from-input"
                    />
                  </div>
                  
                  {/* Date To */}
                  <div>
                    <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Discovered To</Label>
                    <Input
                      type="date"
                      value={dateRange.to}
                      onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                      className={`${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                      data-testid="date-to-input"
                    />
                  </div>
                  
                  {/* CWE Filter */}
                  <div>
                    <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>CWE ID</Label>
                    <Input
                      placeholder="e.g., CWE-79"
                      value={cweFilter}
                      onChange={(e) => setCweFilter(e.target.value)}
                      className={`${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
                      data-testid="cwe-filter-input"
                    />
                  </div>
                  
                  {/* Clear Filters */}
                  <div className="flex items-end">
                    <Button 
                      variant="outline" 
                      onClick={clearAdvancedFilters}
                      className={`w-full ${isDarkMode ? 'border-white/20 text-white hover:bg-white/10' : ''}`}
                      data-testid="clear-advanced-filters-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>
                  </div>
                </div>
                
                {/* Active Filters Display */}
                {(cvssRange[0] > 0 || cvssRange[1] < 10 || epssRange[0] > 0 || epssRange[1] < 100 || 
                  dateRange.from || dateRange.to || assigneeFilter !== 'all' || cweFilter || exploitedFilter !== 'all') && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Active filters:</span>
                    {(cvssRange[0] > 0 || cvssRange[1] < 10) && (
                      <Badge variant="secondary" className="text-xs">CVSS: {cvssRange[0]}-{cvssRange[1]}</Badge>
                    )}
                    {(epssRange[0] > 0 || epssRange[1] < 100) && (
                      <Badge variant="secondary" className="text-xs">EPSS: {epssRange[0]}%-{epssRange[1]}%</Badge>
                    )}
                    {dateRange.from && <Badge variant="secondary" className="text-xs">From: {dateRange.from}</Badge>}
                    {dateRange.to && <Badge variant="secondary" className="text-xs">To: {dateRange.to}</Badge>}
                    {assigneeFilter !== 'all' && <Badge variant="secondary" className="text-xs">Assignee: {assigneeFilter}</Badge>}
                    {cweFilter && <Badge variant="secondary" className="text-xs">CWE: {cweFilter}</Badge>}
                    {exploitedFilter !== 'all' && <Badge variant="secondary" className="text-xs">Exploited: {exploitedFilter}</Badge>}
                  </div>
                )}
              </CollapsibleContent>
            </div>
          </Collapsible>
          
          {/* Bulk Actions Bar */}
          {selectedVulns.size > 0 && (
            <div className={`rounded-xl p-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  <strong>{selectedVulns.size}</strong> selected
                </span>
                <Button size="sm" variant="outline" onClick={() => setSelectedVulns(new Set())} className={`h-8 ${isDarkMode ? 'border-white/10 text-zinc-300' : 'border-slate-300 text-slate-700'}`}>
                  Clear
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => handleBulkAction('mark_remediation')} className="bg-yellow-600 hover:bg-yellow-500 h-8">
                  <Clock className="w-3 h-3 mr-1" />
                  In Progress
                </Button>
                <Button size="sm" onClick={() => handleBulkAction('mark_closed')} className="bg-green-600 hover:bg-green-500 h-8">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Closed
                </Button>
                <Button size="sm" onClick={() => handleBulkAction('assign')} className="bg-indigo-600 hover:bg-indigo-500 h-8">
                  <UserPlus className="w-3 h-3 mr-1" />
                  Assign
                </Button>
                <Button size="sm" onClick={() => handleBulkAction('export')} className="bg-cyan-600 hover:bg-cyan-500 h-8">
                  <Download className="w-3 h-3 mr-1" />
                  Export
                </Button>
              </div>
            </div>
          )}

          <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
            <FilterBar {...filterBarProps} extraFilters={
              <Button onClick={() => handleExportCSV('list')} className="bg-emerald-600 hover:bg-emerald-500" size="sm">
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            } />

            {/* Select All */}
            <div className={`flex items-center gap-2 mb-4 pb-3 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
              <Checkbox 
                checked={selectedVulns.size === filteredVulnerabilities.length && filteredVulnerabilities.length > 0}
                onCheckedChange={selectAllFiltered}
                className={isDarkMode ? 'border-white/20' : 'border-slate-300'}
              />
              <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Select All ({filteredVulnerabilities.length})</span>
            </div>

            <div className="space-y-3">
              {filteredVulnerabilities.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                  <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No vulnerabilities found</p>
                </div>
              ) : (
                filteredVulnerabilities.map((vuln) => (
                  <VulnerabilityCard key={vuln.id} vuln={vuln} showCheckbox={true} />
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* By Asset Tab */}
        <TabsContent value="by-asset" className="mt-5">
          <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-5">
              <div>
                <h2 className={`font-rajdhani font-semibold text-lg lg:text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Vulnerabilities by Asset</h2>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{filteredByAsset.length} assets with vulnerabilities</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                <div className="relative flex-1 lg:w-64">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                  <Input
                    placeholder="Search assets..."
                    value={assetTabSearch}
                    onChange={(e) => setAssetTabSearch(e.target.value)}
                    className={`pl-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  />
                </div>
                <Button onClick={() => handleExportCSV('by-asset')} className="bg-emerald-600 hover:bg-emerald-500" size="sm">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {filteredByAsset.length === 0 ? (
                <div className="text-center py-12">
                  <Server className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No assets found</p>
                </div>
              ) : (
                filteredByAsset.map((item) => (
                  <Collapsible 
                    key={item.asset.id} 
                    open={expandedAssets[item.asset.id]}
                    onOpenChange={() => toggleAssetExpand(item.asset.id)}
                  >
                    <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'glass-hover border-white/10' : 'bg-white border-slate-200 hover:border-indigo-300'}`}>
                      <CollapsibleTrigger className="w-full">
                        <div className={`p-3 lg:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                          <div className="flex items-center gap-3 lg:gap-4">
                            <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center ${
                              item.critical_count > 0 ? 'bg-red-500/20' :
                              item.high_count > 0 ? 'bg-orange-500/20' :
                              'bg-emerald-500/20'
                            }`}>
                              <Server className={`w-4 h-4 lg:w-5 lg:h-5 ${
                                item.critical_count > 0 ? 'text-red-400' :
                                item.high_count > 0 ? 'text-orange-400' :
                                'text-emerald-400'
                              }`} />
                            </div>
                            <div className="text-left">
                              <p className={`font-rajdhani font-semibold text-sm lg:text-base ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.asset.name}</p>
                              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{item.asset.type} • {item.asset.ip_address || 'No IP'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 lg:gap-4">
                            <div className="flex flex-wrap gap-1 lg:gap-2">
                              {item.critical_count > 0 && (
                                <span className="px-1.5 lg:px-2 py-0.5 lg:py-1 bg-red-500/20 text-red-400 rounded text-[10px] lg:text-xs">
                                  {item.critical_count} Crit
                                </span>
                              )}
                              {item.high_count > 0 && (
                                <span className="px-1.5 lg:px-2 py-0.5 lg:py-1 bg-orange-500/20 text-orange-400 rounded text-[10px] lg:text-xs">
                                  {item.high_count} High
                                </span>
                              )}
                              {item.medium_count > 0 && (
                                <span className="px-1.5 lg:px-2 py-0.5 lg:py-1 bg-yellow-500/20 text-yellow-400 rounded text-[10px] lg:text-xs">
                                  {item.medium_count} Med
                                </span>
                              )}
                            </div>
                            <div className="px-2 lg:px-3 py-0.5 lg:py-1 bg-indigo-500/20 text-indigo-400 rounded font-mono text-xs lg:text-sm">
                              {item.vulnerability_count}
                            </div>
                            <ChevronRight className={`w-4 h-4 lg:w-5 lg:h-5 transition-transform ${isDarkMode ? 'text-zinc-400' : 'text-slate-400'} ${expandedAssets[item.asset.id] ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className={`border-t p-3 lg:p-4 ${isDarkMode ? 'border-white/10 bg-zinc-900/50' : 'border-slate-200 bg-slate-50'}`}>
                          <div className="space-y-2">
                            {item.vulnerabilities.map((vuln) => {
                              const sla = calculateSlaStatus(vuln);
                              const assessmentType = vuln.assessment_type || vuln.scan_type || vuln.source;
                              return (
                                <div 
                                  key={vuln.id} 
                                  onClick={() => navigate(`/vulnerability/${vuln.id}`)}
                                  className={`p-2 lg:p-3 rounded-lg cursor-pointer transition-colors ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-700/50' : 'bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm'}`}
                                >
                                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 lg:gap-3 flex-1">
                                      <span className={`w-2 h-2 rounded-full shrink-0 ${
                                        vuln.severity === 'critical' ? 'bg-red-400' :
                                        vuln.severity === 'high' ? 'bg-orange-400' :
                                        vuln.severity === 'medium' ? 'bg-yellow-400' : 'bg-green-400'
                                      }`} />
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-xs lg:text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{vuln.title}</p>
                                        <div className="flex flex-wrap items-center gap-1 mt-1">
                                          {vuln.cve_id && <span className={`text-[10px] lg:text-xs font-mono ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{vuln.cve_id}</span>}
                                          {vuln.cwe && <span className={`text-[10px] lg:text-xs font-mono ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{vuln.cwe}</span>}
                                          {assessmentType && (
                                            <span className={`text-[10px] px-1 py-0.5 rounded ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                                              {getAssessmentTypeLabel(assessmentType)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] lg:text-xs uppercase border ${getSeverityColor(vuln.severity)}`}>
                                        {vuln.severity}
                                      </span>
                                      {vuln.cvss_score && (
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] lg:text-xs font-mono ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                                          CVSS: {vuln.cvss_score}
                                        </span>
                                      )}
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] lg:text-xs border ${getSlaStatusColor(sla.status)}`}>
                                        {getSlaStatusLabel(sla.status, sla.daysRemaining)}
                                      </span>
                                      <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* By Source Tab */}
        <TabsContent value="by-assessment" className="mt-5">
          <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-5">
              <div>
                <h2 className={`font-rajdhani font-semibold text-lg lg:text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Vulnerabilities by Source</h2>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Grouped by scan/assessment method</p>
              </div>
              <div className="relative w-full lg:w-64">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                <Input
                  placeholder="Search sources..."
                  value={sourceTabSearch}
                  onChange={(e) => setSourceTabSearch(e.target.value)}
                  className={`pl-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                />
              </div>
            </div>

            <div className="space-y-3">
              {filteredBySource.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardList className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                  <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No assessment data available</p>
                </div>
              ) : (
                filteredBySource.map((group) => (
                  <Collapsible 
                    key={group.category} 
                    open={expandedAssets[`source-${group.category}`]}
                    onOpenChange={() => toggleAssetExpand(`source-${group.category}`)}
                  >
                    <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'glass-hover border-white/10' : 'bg-white border-slate-200 hover:border-indigo-300'}`}>
                      <CollapsibleTrigger className="w-full">
                        <div className={`p-3 lg:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                          <div className="flex items-center gap-3 lg:gap-4">
                            <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center ${
                              group.category === 'Code Analysis' ? 'bg-emerald-500/20' :
                              group.category === 'SBOM Analysis' ? 'bg-blue-500/20' :
                              group.category === 'SecureProbe Scans' ? 'bg-red-500/20' : 'bg-violet-500/20'
                            }`}>
                              {group.category === 'Code Analysis' ? <Code className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-400" /> :
                               group.category === 'SBOM Analysis' ? <Shield className="w-4 h-4 lg:w-5 lg:h-5 text-blue-400" /> :
                               group.category === 'SecureProbe Scans' ? <Target className="w-4 h-4 lg:w-5 lg:h-5 text-red-400" /> :
                               <ClipboardList className="w-4 h-4 lg:w-5 lg:h-5 text-violet-400" />}
                            </div>
                            <div className="text-left">
                              <p className={`font-rajdhani font-semibold text-sm lg:text-base ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                {group.category}
                              </p>
                              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                                {group.items.length} assessments • {group.vulnerability_count} vulnerabilities
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 lg:gap-4">
                            <div className="flex flex-wrap gap-1 lg:gap-2">
                              {group.critical_count > 0 && (
                                <span className="px-1.5 lg:px-2 py-0.5 lg:py-1 bg-red-500/20 text-red-400 rounded text-[10px] lg:text-xs">
                                  {group.critical_count} Crit
                                </span>
                              )}
                              {group.high_count > 0 && (
                                <span className="px-1.5 lg:px-2 py-0.5 lg:py-1 bg-orange-500/20 text-orange-400 rounded text-[10px] lg:text-xs">
                                  {group.high_count} High
                                </span>
                              )}
                            </div>
                            <div className="px-2 lg:px-3 py-0.5 lg:py-1 bg-violet-500/20 text-violet-400 rounded font-mono text-xs lg:text-sm">
                              {group.vulnerability_count}
                            </div>
                            <ChevronRight className={`w-4 h-4 lg:w-5 lg:h-5 transition-transform ${isDarkMode ? 'text-zinc-400' : 'text-slate-400'} ${expandedAssets[`source-${group.category}`] ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className={`border-t p-3 lg:p-4 ${isDarkMode ? 'border-white/10 bg-zinc-900/50' : 'border-slate-200 bg-slate-50'}`}>
                          <div className="space-y-3">
                            {group.items.map((item, itemIdx) => (
                              <div key={itemIdx} className={`rounded-lg border ${isDarkMode ? 'bg-zinc-800/50 border-white/5' : 'bg-white border-slate-200'}`}>
                                <div className={`p-3 ${isDarkMode ? 'hover:bg-zinc-700/30' : 'hover:bg-slate-50'}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                      {item.assessment_name || item.assessment_type}
                                    </p>
                                    <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-600'}`}>
                                      {item.vulnerability_count} issues
                                    </span>
                                  </div>
                                  <div className="space-y-1">
                                    {item.vulnerabilities?.slice(0, 3).map((vuln) => (
                                      <div 
                                        key={vuln.id}
                                        onClick={() => navigate(`/vulnerability/${vuln.id}`)}
                                        className={`flex items-center gap-2 p-2 rounded cursor-pointer ${isDarkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-slate-100'}`}
                                      >
                                        <span className={`w-2 h-2 rounded-full ${
                                          vuln.severity === 'critical' ? 'bg-red-400' :
                                          vuln.severity === 'high' ? 'bg-orange-400' :
                                          vuln.severity === 'medium' ? 'bg-yellow-400' : 'bg-green-400'
                                        }`} />
                                        <span className={`text-xs truncate flex-1 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{vuln.title}</span>
                                        <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${getSeverityColor(vuln.severity)}`}>{vuln.severity}</span>
                                      </div>
                                    ))}
                                    {item.vulnerabilities?.length > 3 && (
                                      <p className={`text-xs text-center py-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                                        +{item.vulnerabilities.length - 3} more vulnerabilities
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Remediation Tab */}
        <TabsContent value="remediation" className="mt-5">
          <RemediationPanel isDarkMode={isDarkMode} vulnerabilities={filteredVulnerabilities} />
        </TabsContent>

        {/* Correlation Tab */}
        <TabsContent value="correlation" className="mt-5">
          <CorrelationPanel isDarkMode={isDarkMode} />
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
              <h3 className={`font-rajdhani font-semibold text-base lg:text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Vulnerability Trend (7 Days)</h3>
              <div className="h-48 lg:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'} />
                    <XAxis dataKey="name" stroke={isDarkMode ? '#71717a' : '#64748b'} fontSize={10} />
                    <YAxis stroke={isDarkMode ? '#71717a' : '#64748b'} fontSize={10} />
                    <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#ffffff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', color: isDarkMode ? '#fff' : '#1e293b' }} />
                    <Line type="monotone" dataKey="critical" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444' }} />
                    <Line type="monotone" dataKey="high" stroke="#f97316" strokeWidth={2} dot={{ fill: '#f97316' }} />
                    <Line type="monotone" dataKey="medium" stroke="#eab308" strokeWidth={2} dot={{ fill: '#eab308' }} />
                    <Line type="monotone" dataKey="low" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={`rounded-2xl p-4 lg:p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
              <h3 className={`font-rajdhani font-semibold text-base lg:text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>SLA Performance</h3>
              <div className="h-48 lg:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'On Track', value: slaStats.onTrack, fill: '#22c55e' },
                    { name: 'Critical', value: slaStats.criticalSoon, fill: '#f97316' },
                    { name: 'Overdue', value: slaStats.overdue, fill: '#ef4444' },
                    { name: 'Fixed', value: slaStats.fixedOnTime, fill: '#10b981' },
                    { name: 'Late', value: slaStats.fixedLate, fill: '#8b5cf6' },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'} />
                    <XAxis dataKey="name" stroke={isDarkMode ? '#71717a' : '#64748b'} fontSize={10} />
                    <YAxis stroke={isDarkMode ? '#71717a' : '#64748b'} fontSize={10} />
                    <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#ffffff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', color: isDarkMode ? '#fff' : '#1e293b' }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {[
                        { name: 'On Track', fill: '#22c55e' },
                        { name: 'Critical', fill: '#f97316' },
                        { name: 'Overdue', fill: '#ef4444' },
                        { name: 'Fixed', fill: '#10b981' },
                        { name: 'Late', fill: '#8b5cf6' },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* SLA Settings Dialog */}
      <Dialog open={showSlaSettings} onOpenChange={setShowSlaSettings}>
        <DialogContent className={`max-w-md ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`font-rajdhani text-xl flex items-center gap-2 ${isDarkMode ? '' : 'text-slate-900'}`}>
              <Settings className="w-5 h-5 text-indigo-400" />
              SLA Timeline Settings
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>
              Configure remediation deadlines based on severity
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Critical (hours)</Label>
              <Input
                type="number"
                value={slaTimelines.critical}
                onChange={(e) => setSlaTimelines(prev => ({ ...prev, critical: parseInt(e.target.value) || 24 }))}
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Default: 24 hours (1 day)</p>
            </div>
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>High (hours)</Label>
              <Input
                type="number"
                value={slaTimelines.high}
                onChange={(e) => setSlaTimelines(prev => ({ ...prev, high: parseInt(e.target.value) || 168 }))}
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Default: 168 hours (7 days)</p>
            </div>
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Medium (hours)</Label>
              <Input
                type="number"
                value={slaTimelines.medium}
                onChange={(e) => setSlaTimelines(prev => ({ ...prev, medium: parseInt(e.target.value) || 720 }))}
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Default: 720 hours (30 days)</p>
            </div>
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Low (hours)</Label>
              <Input
                type="number"
                value={slaTimelines.low}
                onChange={(e) => setSlaTimelines(prev => ({ ...prev, low: parseInt(e.target.value) || 2160 }))}
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Default: 2160 hours (90 days)</p>
            </div>
            
            {/* Email Notifications Section */}
            <div className={`pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
              <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Email Notifications</h4>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Automated SLA alerts are sent daily at 8 AM and 4 PM to admin users when vulnerabilities are approaching or past their SLA deadlines.
              </p>
              <Button
                onClick={async () => {
                  try {
                    const res = await api.post('/sla/check-and-notify');
                    toast.success('SLA notification check completed');
                  } catch (error) {
                    toast.error('Failed to trigger SLA notification');
                  }
                }}
                variant="outline"
                size="sm"
                className="w-full border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Send SLA Alert Now
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlaTimelines(DEFAULT_SLA_TIMELINES)} className="border-white/10 text-zinc-300">
              Reset to Defaults
            </Button>
            <Button onClick={saveSlaSettings} className="bg-indigo-600 hover:bg-indigo-500">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CVE Lookup Dialog */}
      <Dialog open={showCveDialog} onOpenChange={setShowCveDialog}>
        <DialogContent className={`max-w-lg ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`font-rajdhani text-xl ${isDarkMode ? '' : 'text-slate-900'}`}>CVE Details</DialogTitle>
          </DialogHeader>
          {loadingCve ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
          ) : cveDetails ? (
            <div className="space-y-4">
              <div>
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{cveDetails.id}</p>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{cveDetails.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>CVSS Score</p>
                  <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{cveDetails.cvss}</p>
                </div>
                <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>EPSS Score</p>
                  <p className="font-semibold text-violet-400">{((cveDetails.epss || 0) * 100).toFixed(2)}%</p>
                </div>
              </div>
              {cveDetails.references?.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>References</p>
                  {cveDetails.references.slice(0, 3).map((ref, idx) => (
                    <a key={idx} href={ref.url} target="_blank" rel="noopener noreferrer" className="text-cyan-500 text-xs hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> {ref.source}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className={`text-center py-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No CVE details found</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={`font-rajdhani text-xl ${isDarkMode ? '' : 'text-slate-900'}`}>Assign Vulnerabilities</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>
              Assign {selectedVulns.size} vulnerabilities to a team member
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Assignee</Label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  {users.length > 0 ? users.map(user => (
                    <SelectItem key={user.id} value={user.email}>{user.full_name || user.email}</SelectItem>
                  )) : (
                    <>
                      <SelectItem value="security-team@company.com">Security Team</SelectItem>
                      <SelectItem value="dev-team@company.com">Development Team</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Note (optional)</Label>
              <Textarea
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                placeholder="Add a note for the assignee..."
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className={isDarkMode ? 'border-white/10 text-zinc-300' : 'border-slate-300 text-slate-700'}>
              Cancel
            </Button>
            <Button onClick={handleAssign} className="bg-indigo-600 hover:bg-indigo-500">
              <UserPlus className="w-4 h-4 mr-2" />
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Remediation Panel Component (integrated from RemediationFactory)
function RemediationPanel({ isDarkMode, vulnerabilities }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [selectedVuln, setSelectedVuln] = useState(null);
  const [aiFixLoading, setAiFixLoading] = useState(false);
  const [currentAiFix, setCurrentAiFix] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, [taskStatusFilter]);

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (taskStatusFilter !== 'all') params.append('status', taskStatusFilter);
      const response = await api.get(`/remediation-factory/tasks?${params}`);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (vulnId, type = 'manual') => {
    try {
      const response = await api.post('/remediation-factory/tasks', {
        vulnerability_id: vulnId,
        remediation_type: type,
        assigned_team: 'security',
        priority: 'high'
      });
      toast.success('Remediation task created');
      fetchTasks();
      setShowCreateTask(false);
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const generateAiFix = async (vuln) => {
    setSelectedVuln(vuln);
    setAiFixLoading(true);
    try {
      const response = await api.post(`/vulnerabilities/${vuln.id}/ai-remediation`);
      setCurrentAiFix(response.data.ai_remediation);
      toast.success('AI remediation generated');
    } catch (error) {
      toast.error('Failed to generate AI fix');
    } finally {
      setAiFixLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await api.patch(`/remediation-factory/tasks/${taskId}`, { status });
      toast.success(`Task ${status}`);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-emerald-400 bg-emerald-500/20';
      case 'in_progress': return 'text-blue-400 bg-blue-500/20';
      case 'pending': return 'text-yellow-400 bg-yellow-500/20';
      case 'blocked': return 'text-red-400 bg-red-500/20';
      default: return 'text-zinc-400 bg-zinc-500/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200'}`}>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{tasks.length}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total Tasks</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200'}`}>
          <p className="text-2xl font-bold text-blue-500">{tasks.filter(t => t.status === 'in_progress').length}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>In Progress</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200'}`}>
          <p className="text-2xl font-bold text-emerald-500">{tasks.filter(t => t.status === 'completed').length}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Completed</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200'}`}>
          <p className="text-2xl font-bold text-yellow-500">{tasks.filter(t => t.status === 'pending').length}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Pending</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={`p-5 rounded-xl ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Quick Remediation</h3>
          <Select value={taskStatusFilter} onValueChange={setTaskStatusFilter}>
            <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Vulnerabilities needing remediation */}
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {vulnerabilities?.slice(0, 10).map((vuln) => (
            <div key={vuln.id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      vuln.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                      vuln.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                      vuln.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {vuln.severity?.toUpperCase()}
                    </span>
                    <h4 className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{vuln.title}</h4>
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                    {vuln.cve_id || 'No CVE'} • CVSS: {vuln.cvss_score || 'N/A'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => generateAiFix(vuln)}
                    disabled={aiFixLoading && selectedVuln?.id === vuln.id}
                    className={isDarkMode ? 'border-white/10' : ''}
                  >
                    {aiFixLoading && selectedVuln?.id === vuln.id ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> AI...</>
                    ) : (
                      <><Sparkles className="w-3 h-3 mr-1" /> AI Fix</>
                    )}
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => createTask(vuln.id, 'ai_assisted')}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    <Zap className="w-3 h-3 mr-1" /> Create Task
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Remediation Tasks */}
      <div className={`p-5 rounded-xl ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200'}`}>
        <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Active Remediation Tasks</h3>
        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></div>
        ) : tasks.length === 0 ? (
          <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
            <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No remediation tasks yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.slice(0, 8).map((task) => (
              <div key={task.id} className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded text-xs ${getStatusColor(task.status)}`}>
                    {task.status?.replace('_', ' ')}
                  </span>
                  <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{task.title || task.vulnerability_id}</span>
                </div>
                <div className="flex gap-1">
                  {task.status === 'pending' && (
                    <Button size="sm" variant="ghost" onClick={() => updateTaskStatus(task.id, 'in_progress')}>
                      <Play className="w-3 h-3" />
                    </Button>
                  )}
                  {task.status === 'in_progress' && (
                    <Button size="sm" variant="ghost" onClick={() => updateTaskStatus(task.id, 'completed')}>
                      <CheckCircle className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Fix Result Dialog */}
      {currentAiFix && (
        <Dialog open={!!currentAiFix} onOpenChange={() => setCurrentAiFix(null)}>
          <DialogContent className={`max-w-2xl ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-400" />
                AI-Generated Remediation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {currentAiFix.immediate_actions && (
                <div>
                  <h4 className="font-medium mb-2">Immediate Actions</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {currentAiFix.immediate_actions.map((action, i) => (
                      <li key={i} className={isDarkMode ? 'text-zinc-300' : 'text-slate-600'}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
              {currentAiFix.code_fix && (
                <div>
                  <h4 className="font-medium mb-2">Code Fix</h4>
                  <pre className={`p-3 rounded-lg text-xs overflow-x-auto ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                    {currentAiFix.code_fix}
                  </pre>
                </div>
              )}
              {currentAiFix.prevention_measures && (
                <div>
                  <h4 className="font-medium mb-2">Prevention Measures</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {currentAiFix.prevention_measures.map((measure, i) => (
                      <li key={i} className={isDarkMode ? 'text-zinc-300' : 'text-slate-600'}>{measure}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Correlation Panel Component (integrated from CorrelationEngine)
function CorrelationPanel({ isDarkMode }) {
  const [dashboard, setDashboard] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [newTicketTitle, setNewTicketTitle] = useState('');
  const [newTicketPriority, setNewTicketPriority] = useState('high');
  const [newTicketDescription, setNewTicketDescription] = useState('');
  const [creatingTicket, setCreatingTicket] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchTickets();
  }, [ticketStatusFilter]);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/correlation/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch correlation dashboard:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const params = new URLSearchParams();
      if (ticketStatusFilter !== 'all') params.append('status', ticketStatusFilter);
      const response = await api.get(`/correlation/tickets?${params}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCorrelatedTicket = async () => {
    if (!newTicketTitle.trim()) {
      toast.error('Please enter a ticket title');
      return;
    }
    
    setCreatingTicket(true);
    try {
      const response = await api.post('/correlation/tickets', {
        title: newTicketTitle.trim(),
        description: newTicketDescription.trim(),
        priority: newTicketPriority,
        vulnerability_ids: []
      });
      toast.success('Correlated ticket created');
      fetchTickets();
      setShowCreateTicket(false);
      setNewTicketTitle('');
      setNewTicketDescription('');
      setNewTicketPriority('high');
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-blue-400" />;
      case 'open': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      default: return <XCircle className="w-4 h-4 text-zinc-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Correlation Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200'}`}>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{dashboard?.total_correlations || 0}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total Correlations</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200'}`}>
          <p className="text-2xl font-bold text-indigo-500">{dashboard?.active_tickets || 0}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Active Tickets</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200'}`}>
          <p className="text-2xl font-bold text-emerald-500">{dashboard?.resolved_this_week || 0}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Resolved This Week</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-white border border-slate-200'}`}>
          <p className="text-2xl font-bold text-orange-500">{dashboard?.avg_resolution_time || 'N/A'}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Avg Resolution (days)</p>
        </div>
      </div>

      {/* Correlated Tickets */}
      <div className={`p-5 rounded-xl ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Correlated Vulnerability Tickets</h3>
          <div className="flex gap-2">
            <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
              <SelectTrigger className={`w-32 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setShowCreateTicket(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <GitBranch className="w-4 h-4 mr-2" /> Create Ticket
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></div>
        ) : tickets.length === 0 ? (
          <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
            <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No correlated tickets yet</p>
            <p className="text-sm">Create tickets to track related vulnerabilities together</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div key={ticket.id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getStatusIcon(ticket.status)}
                    <div>
                      <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{ticket.title}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                          {ticket.vulnerability_count || 0} vulnerabilities
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          ticket.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                          ticket.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          ticket.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {ticket.priority}
                        </span>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                          {ticket.assigned_team || 'Unassigned'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className={isDarkMode ? 'border-white/10' : ''}>
                    <Eye className="w-3 h-3 mr-1" /> View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Correlation Insights */}
      {dashboard?.correlation_insights && (
        <div className={`p-5 rounded-xl ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200'}`}>
          <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Correlation Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dashboard.correlation_insights.map((insight, idx) => (
              <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-indigo-400" />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{insight.pattern}</span>
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{insight.description}</p>
                <p className="text-xs text-indigo-400 mt-1">{insight.affected_count} affected assets</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Create Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-slate-900'}>Create Correlation Ticket</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>
              Create a ticket to track correlated vulnerabilities
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Ticket Title *</Label>
              <Input
                value={newTicketTitle}
                onChange={(e) => setNewTicketTitle(e.target.value)}
                placeholder="e.g., Critical XSS vulnerabilities across web assets"
                className={isDarkMode ? 'bg-zinc-800 border-white/10 text-white mt-1' : 'mt-1'}
              />
            </div>
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Priority</Label>
              <Select value={newTicketPriority} onValueChange={setNewTicketPriority}>
                <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10 text-white mt-1' : 'mt-1'}>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Description</Label>
              <Textarea
                value={newTicketDescription}
                onChange={(e) => setNewTicketDescription(e.target.value)}
                placeholder="Describe the correlated vulnerabilities and recommended actions..."
                rows={4}
                className={isDarkMode ? 'bg-zinc-800 border-white/10 text-white mt-1' : 'mt-1'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateTicket(false)} className={isDarkMode ? 'text-zinc-400' : ''}>
              Cancel
            </Button>
            <Button onClick={createCorrelatedTicket} disabled={creatingTicket || !newTicketTitle.trim()} className="bg-indigo-600 hover:bg-indigo-700">
              {creatingTicket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitBranch className="w-4 h-4 mr-2" />}
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
